import React from 'react';
import { urlBase64ToUint8Array } from "./SecRapports.jsx";
import { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { SUPA_URL, SUPA_KEY, VAPID_PUBLIC_KEY} from "../constants.js";
import { fd, today, fname, now, contratsOf, contratActif, dernierContrat, pointageTache} from "../utils.js";
import { Ic } from "./Ic.jsx";
import { M } from "./M.jsx";
import { supaGet, supaSet, useSt, syncBus, SUPA_TOKEN, supaSaveSession, SUPA_REFRESH, sendAccessEmail, supaAuthHeader, supaRefreshToken} from "../supabase.js";
import { OT_STATUS } from "./Facturation.jsx";
import { NOTIF_EVENTS, defaultNotifConfig, notifToastStore, notifContextRef, setNotifContext, notifEventBus } from '../notifBus.js';
import { OTQuick } from './OTQuick.jsx';


// Composant admin — registre complet
// ══════════════ EXPÉDITION OBJETS TROUVÉS — PHASE 1 ══════════════

// Grille tarifaire par défaut (coûts réels La Poste/Chronopost 2026)
// Modifiable depuis le panneau admin — stocké dans tarifsExp7
//
// Structure :
//   colissimo.france      : tranches par poids (250g→10kg), livraison domicile France métro
//   colissimo.europe      : tranches (500g→10kg), UE + Suisse + Monaco
//   colissimo.outremer1   : tranches (500g→10kg), OM zone 1
//   colissimo.outremer2   : tranches (500g→10kg), OM zone 2
//   colissimo.mondeB      : tranches (500g→10kg), Zone B (Amériques, Moyen-Orient, Afrique…)
//   colissimo.mondeC      : tranches (500g→10kg), Zone C (Asie, Pacifique…)
//   chronopost.france     : tranches (0–10kg), Chrono 13 domicile
//   chronopost.z1…z9      : tranches (0–10kg), Chrono Express international
//
// Sélection destination côté UX : pays → {transporteurZone} via PAYS_ZONES
// Au-delà de 10 kg → message "nous contacter"

export const TARIFS_EXP_DEFAULT = {
  margeMode: 'pct',   // 'pct' | 'fixe' | 'cumul'
  margePct:  25,      // % appliqué sur coût transporteur HT
  margeFixe: 5.00,    // montant fixe HT en € (modes fixe ou cumul)

  // ── COLISSIMO ─────────────────────────────────────────────────────────────
  // Tarifs domicile (affranchissement particulier laposte.fr 2026)
  // Tranche : [250g, 500g, 750g, 1kg, 2kg, 5kg, 10kg]
  colissimo: {
    france:    [5.49, 7.59, 9.29, 9.59, 11.19, 17.39, 25.29],
    // Europe (UE + Suisse) — pas de tranche 250g ni 750g
    // Tranche : [500g, 1kg, 2kg, 5kg, 10kg, 15kg] → on tronque à 10kg (idx 0→4)
    europe:    [14.99, 19.39, 22.19, 28.59, 46.99],
    // Outre-mer OM1 (Guadeloupe, Martinique, Réunion, Guyane, Mayotte, SPM, St-Martin, St-Barth)
    // Tranche : [500g, 1kg, 2kg, 5kg, 10kg]
    outremer1: [12.02, 19.00, 25.89, 38.90, 62.32],
    // Outre-mer OM2
    outremer2: [12.21, 18.95, 33.49, 55.96, 109.58],
    // Monde Zone B (Amériques, Afrique, Moyen-Orient, Asie centrale…)
    // Tranche : [500g, 1kg, 2kg, 5kg, 10kg]
    mondeB:    [23.79, 28.39, 31.09, 39.89, 66.09],
    // Monde Zone C (Asie du Sud-Est, Japon, Corée, Australie, Nouvelle-Zélande, Pacifique…)
    mondeC:    [35.19, 39.19, 53.99, 78.69, 148.99],
  },

  // ── CHRONOPOST CHRONO EXPRESS ──────────────────────────────────────────────
  // Tarifs TTC (particulier laposte.fr 2026) — livraison domicile uniquement
  // Tranches communes : [0-1kg, 1-3kg, 3-10kg, 10kg+] — on affiche jusqu'à 10kg
  chronopost: {
    france: [23.40, 25.20, 28.80, 40.80],  // Chrono 13, tranches [1kg,3kg,10kg,20kg]
    z1:  [29.00, 43.00,  79.00, 159.00],   // UE proche : Allemagne, Belgique, Luxembourg, Pays-Bas, Autriche
    z2:  [32.00, 45.00,  82.00, 164.00],   // UE standard : Espagne, Italie, Portugal, Pologne, Tchéquie…
    z3:  [42.00, 54.00,  99.00, 205.00],   // UE éloignée + UK : Grèce, Roumanie, Bulgarie, Croatie, Royaume-Uni
    z4:  [47.00, 67.00, 130.00, 260.00],   // Proche international : États-Unis, Canada, Turquie, Israël, Afrique Nord
    z5:  [47.00, 67.00, 120.00, 264.00],   // Moyen-Orient, Afrique subsaharienne
    z6:  [56.00, 92.00, 185.00, 364.00],   // Asie du Sud, Afrique centrale
    z7:  [62.00, 102.00, 207.00, 404.00],  // Japon, Australie, Nouvelle-Zélande, Chine, Taiwan, Corée
    z8:  [58.00, 88.00, 155.00, 280.00],   // Outre-mer (Guadeloupe, Martinique, Réunion…)
    z9:  [80.00, 155.00, 260.00, 520.00],  // Outre-mer éloigné (Polynésie française, Saint-Pierre-et-Miquelon)
  },
};

// ── TABLE PAYS → ZONE ──────────────────────────────────────────────────────
// Couvre ~60 nationalités fréquentes à Colmar + "Autre"
// Format : { label, colissimo: 'france'|'europe'|'outremer1'|'outremer2'|'mondeB'|'mondeC'|null,
//            chronopost: 'france'|'z1'…'z9'|null }
// null = non desservi par ce transporteur pour cette destination
export const PAYS_ZONES = [
  // France & DOM-TOM
  { label:'France métropolitaine',     colissimo:'france',    chronopost:'france' },
  { label:'Guadeloupe',                colissimo:'outremer1', chronopost:'z8' },
  { label:'Martinique',                colissimo:'outremer1', chronopost:'z8' },
  { label:'Réunion',                   colissimo:'outremer1', chronopost:'z8' },
  { label:'Guyane',                    colissimo:'outremer1', chronopost:'z8' },
  { label:'Mayotte',                   colissimo:'outremer1', chronopost:'z8' },
  { label:'Polynésie française',       colissimo:'outremer2', chronopost:'z9' },
  { label:'Saint-Pierre-et-Miquelon',  colissimo:'outremer1', chronopost:'z9' },
  { label:'Saint-Martin',              colissimo:'outremer1', chronopost:'z8' },
  { label:'Saint-Barthélemy',          colissimo:'outremer1', chronopost:'z8' },
  // Europe Z1 (UE proche)
  { label:'Allemagne',    colissimo:'europe', chronopost:'z1' },
  { label:'Belgique',     colissimo:'europe', chronopost:'z1' },
  { label:'Luxembourg',   colissimo:'europe', chronopost:'z1' },
  { label:'Pays-Bas',     colissimo:'europe', chronopost:'z1' },
  { label:'Autriche',     colissimo:'europe', chronopost:'z1' },
  { label:'Suisse',       colissimo:'europe', chronopost:'z1' },
  { label:'Andorre',      colissimo:'france',  chronopost:'z1' },
  // Europe Z2 (UE standard)
  { label:'Espagne',      colissimo:'europe', chronopost:'z2' },
  { label:'Italie',       colissimo:'europe', chronopost:'z2' },
  { label:'Portugal',     colissimo:'europe', chronopost:'z2' },
  { label:'Pologne',      colissimo:'europe', chronopost:'z2' },
  { label:'République Tchèque', colissimo:'europe', chronopost:'z2' },
  { label:'Slovaquie',    colissimo:'europe', chronopost:'z2' },
  { label:'Hongrie',      colissimo:'europe', chronopost:'z2' },
  { label:'Danemark',     colissimo:'europe', chronopost:'z2' },
  { label:'Finlande',     colissimo:'europe', chronopost:'z2' },
  { label:'Suède',        colissimo:'europe', chronopost:'z2' },
  { label:'Irlande',      colissimo:'europe', chronopost:'z2' },
  { label:'Slovénie',     colissimo:'europe', chronopost:'z2' },
  { label:'Lituanie',     colissimo:'europe', chronopost:'z2' },
  { label:'Lettonie',     colissimo:'europe', chronopost:'z2' },
  { label:'Estonie',      colissimo:'europe', chronopost:'z2' },
  // Europe Z3 (UE éloignée + UK)
  { label:'Royaume-Uni',  colissimo:'europe', chronopost:'z3' },
  { label:'Grèce',        colissimo:'europe', chronopost:'z3' },
  { label:'Roumanie',     colissimo:'europe', chronopost:'z3' },
  { label:'Bulgarie',     colissimo:'europe', chronopost:'z3' },
  { label:'Croatie',      colissimo:'europe', chronopost:'z3' },
  { label:'Norvège',      colissimo:'europe', chronopost:'z3' },
  { label:'Liechtenstein',colissimo:'europe', chronopost:'z3' },
  // Amériques / monde Z4-Z5
  { label:'États-Unis',   colissimo:'mondeB', chronopost:'z4' },
  { label:'Canada',       colissimo:'mondeB', chronopost:'z4' },
  { label:'Mexique',      colissimo:'mondeB', chronopost:'z5' },
  { label:'Brésil',       colissimo:'mondeB', chronopost:'z5' },
  { label:'Argentine',    colissimo:'mondeB', chronopost:'z5' },
  { label:'Chili',        colissimo:'mondeB', chronopost:'z5' },
  { label:'Colombie',     colissimo:'mondeB', chronopost:'z5' },
  { label:'Pérou',        colissimo:'mondeB', chronopost:'z5' },
  // Moyen-Orient / Afrique Z4-Z6
  { label:'Maroc',        colissimo:'mondeB', chronopost:'z4' },
  { label:'Tunisie',      colissimo:'mondeB', chronopost:'z4' },
  { label:'Algérie',      colissimo:'mondeB', chronopost:'z4' },
  { label:'Israël',       colissimo:'mondeB', chronopost:'z4' },
  { label:'Turquie',      colissimo:'mondeB', chronopost:'z4' },
  { label:'Afrique du Sud', colissimo:'mondeB', chronopost:'z5' },
  // Asie Z6-Z7
  { label:'Inde',         colissimo:'mondeB', chronopost:'z6' },
  { label:'Thaïlande',    colissimo:'mondeC', chronopost:'z6' },
  { label:'Viêt Nam',     colissimo:'mondeC', chronopost:'z6' },
  { label:'Singapour',    colissimo:'mondeC', chronopost:'z7' },
  { label:'Hong Kong',    colissimo:'mondeC', chronopost:'z7' },
  { label:'Chine',        colissimo:'mondeC', chronopost:'z7' },
  { label:'Taiwan',       colissimo:'mondeC', chronopost:'z7' },
  { label:'Japon',        colissimo:'mondeC', chronopost:'z7' },
  { label:'Corée du Sud', colissimo:'mondeC', chronopost:'z7' },
  { label:'Indonésie',    colissimo:'mondeC', chronopost:'z7' },
  { label:'Philippines',  colissimo:'mondeC', chronopost:'z7' },
  { label:'Malaisie',     colissimo:'mondeC', chronopost:'z7' },
  // Pacifique Z7
  { label:'Australie',           colissimo:'mondeC', chronopost:'z7' },
  { label:'Nouvelle-Zélande',    colissimo:'mondeC', chronopost:'z7' },
  // Sentinel "Autre"
  { label:'Autre pays (saisir zone manuellement)', colissimo:null, chronopost:null },
];

// Libellés affichés côté client (devis, impression)
const COLISSIMO_LABELS = {
  france:    'Colissimo France — livraison J+2',
  europe:    'Colissimo Europe — livraison J+4',
  outremer1: 'Colissimo Outre-mer — livraison 6–8 jours',
  outremer2: 'Colissimo Outre-mer — livraison 6–18 jours',
  mondeB:    'Colissimo International — livraison 3–8 jours',
  mondeC:    'Colissimo International — livraison 3–8 jours',
};
const CHRONOPOST_LABELS = {
  france: 'Chronopost Chrono 13 — livraison lendemain avant 13h',
  z1: 'Chronopost Express — 1 à 2 jours',
  z2: 'Chronopost Express — 1 à 3 jours',
  z3: 'Chronopost Express — 2 à 3 jours',
  z4: 'Chronopost Express — 1 à 3 jours',
  z5: 'Chronopost Express — 2 à 5 jours',
  z6: 'Chronopost Express — 2 à 5 jours',
  z7: 'Chronopost Express — 2 à 5 jours',
  z8: 'Chronopost Express Outre-mer — 3 à 5 jours',
  z9: 'Chronopost Express Outre-mer — 3 à 7 jours',
};

// Tranches par transporteur (labels + max en grammes)
const TRANCHES_COLISSIMO_FRANCE = [
  {label:'jusqu\'à 250 g', max:250},
  {label:'jusqu\'à 500 g', max:500},
  {label:'jusqu\'à 750 g', max:750},
  {label:'jusqu\'à 1 kg',  max:1000},
  {label:'jusqu\'à 2 kg',  max:2000},
  {label:'jusqu\'à 5 kg',  max:5000},
  {label:'jusqu\'à 10 kg', max:10000},
];
const TRANCHES_COLISSIMO_INTER = [
  {label:'jusqu\'à 500 g', max:500},
  {label:'jusqu\'à 1 kg',  max:1000},
  {label:'jusqu\'à 2 kg',  max:2000},
  {label:'jusqu\'à 5 kg',  max:5000},
  {label:'jusqu\'à 10 kg', max:10000},
];
const TRANCHES_CHRONOPOST = [
  {label:'jusqu\'à 1 kg',  max:1000},
  {label:'jusqu\'à 3 kg',  max:3000},
  {label:'jusqu\'à 10 kg', max:10000},
  {label:'jusqu\'à 20 kg', max:20000},
];

// Calcule le prix client HT selon le mode de marge
function applyMarge(cout, t) {
  const mode = t.margeMode || 'pct';
  const pct  = t.margePct  ?? 25;
  const fixe = t.margeFixe ?? 0;
  if (mode === 'pct')   return cout * (1 + pct / 100);
  if (mode === 'fixe')  return cout + fixe;
  if (mode === 'cumul') return cout * (1 + pct / 100) + fixe;
  return cout * (1 + pct / 100);
}

// calcDevis — transporteur = 'colissimo' | 'chronopost'
//             zone = clé dans t.colissimo ou t.chronopost (ex: 'france', 'europe', 'z7'…)
function calcDevis(poids, zone, transporteur, tarifs) {
  const t = tarifs || TARIFS_EXP_DEFAULT;
  const grille = t[transporteur]?.[zone];
  if (!grille) return null;
  const tranches = transporteur === 'chronopost'
    ? TRANCHES_CHRONOPOST
    : zone === 'france' ? TRANCHES_COLISSIMO_FRANCE : TRANCHES_COLISSIMO_INTER;
  const idx = tranches.findIndex(tr => poids <= tr.max);
  if (idx === -1) return { overMax: true };
  const cout = grille[idx];
  if (cout == null) return null;
  const prixHT  = applyMarge(cout, t);
  const prixTTC = prixHT * 1.20;
  return {
    tranche:  tranches[idx].label,
    cout:     cout.toFixed(2),
    prixHT:   prixHT.toFixed(2),
    prixTTC:  prixTTC.toFixed(2),
  };
}

function AdminGrilleTarifs({ tarifsExp, setTarifsExp }) {
  const t = tarifsExp || TARIFS_EXP_DEFAULT;
  const [margeMode, setMargeMode] = useState(t.margeMode || 'pct');
  const [margePct,  setMargePct]  = useState(t.margePct  ?? 25);
  const [margeFixe, setMargeFixe] = useState(t.margeFixe ?? 5);
  const [saved, setSaved]   = useState(false);

  const save = () => {
    setTarifsExp({ ...t, margeMode, margePct, margeFixe });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const exCout = 9.59;
  const exHT = applyMarge(exCout, { margeMode, margePct, margeFixe });
  const exTTC = (exHT * 1.20).toFixed(2);
  const modeLabels = { pct:'% uniquement', fixe:'Montant fixe uniquement', cumul:'% + montant fixe cumulés' };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16, maxWidth:680 }}>
      <div style={{ fontWeight:700, fontSize:16 }}>⚙️ Paramètres d'expédition objets trouvés</div>

      {/* Mode de marge */}
      <div style={{ background:'var(--cr)', borderRadius:10, padding:'14px 16px' }}>
        <div style={{ fontWeight:600, fontSize:13, marginBottom:10 }}>Mode de calcul de la prestation</div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
          {Object.entries(modeLabels).map(([k,v]) => (
            <label key={k} style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer',
              background: margeMode===k ? 'var(--or)' : 'var(--bg)', color: margeMode===k ? 'white' : 'inherit',
              border: '1.5px solid var(--or)', borderRadius:20, padding:'4px 12px' }}>
              <input type="radio" name="margeMode" value={k}
                checked={margeMode===k} onChange={() => setMargeMode(k)}
                style={{ display:'none' }} />
              {v}
            </label>
          ))}
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <div>
            <label className="fl">Pourcentage (%)</label>
            <input type="number" min="0" max="200" step="0.5" value={margePct}
              onChange={e => setMargePct(parseFloat(e.target.value)||0)}
              disabled={margeMode==='fixe'} style={{ width:'100%' }} />
          </div>
          <div>
            <label className="fl">Montant fixe (€ HT)</label>
            <input type="number" min="0" step="0.5" value={margeFixe}
              onChange={e => setMargeFixe(parseFloat(e.target.value)||0)}
              disabled={margeMode==='pct'} style={{ width:'100%' }} />
          </div>
        </div>
        <div style={{ marginTop:10, fontSize:12, color:'var(--si)', background:'var(--bg)', borderRadius:8, padding:'8px 12px' }}>
          Exemple : Colissimo France 1 kg (coût {exCout} €) → <strong>{exHT.toFixed(2)} € HT / {exTTC} € TTC</strong> facturé au client
        </div>
      </div>

      {/* Grille tarifaire affichée en lecture */}
      <div style={{ background:'var(--cr)', borderRadius:10, padding:'14px 16px' }}>
        <div style={{ fontWeight:600, fontSize:13, marginBottom:8 }}>Grille tarifaire en vigueur (2026 — La Poste)</div>
        <div style={{ fontSize:12, color:'var(--si)', marginBottom:10 }}>
          Les tarifs transporteurs sont fixes (barèmes officiels La Poste). Seule la marge ci-dessus est paramétrable.
        </div>

        {/* Colissimo France */}
        <div style={{ marginBottom:10 }}>
          <div style={{ fontWeight:600, fontSize:12, color:'var(--or)', marginBottom:4 }}>Colissimo France (domicile)</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            {TRANCHES_COLISSIMO_FRANCE.map((tr, i) => (
              <div key={i} style={{ background:'var(--bg)', borderRadius:6, padding:'4px 10px', fontSize:12 }}>
                {tr.label} → <strong>{TARIFS_EXP_DEFAULT.colissimo.france[i]} €</strong>
              </div>
            ))}
          </div>
        </div>

        {/* Colissimo Europe */}
        <div style={{ marginBottom:10 }}>
          <div style={{ fontWeight:600, fontSize:12, color:'var(--or)', marginBottom:4 }}>Colissimo Europe (UE + Suisse)</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            {TRANCHES_COLISSIMO_INTER.map((tr, i) => (
              <div key={i} style={{ background:'var(--bg)', borderRadius:6, padding:'4px 10px', fontSize:12 }}>
                {tr.label} → <strong>{TARIFS_EXP_DEFAULT.colissimo.europe[i]} €</strong>
              </div>
            ))}
          </div>
        </div>

        {/* Colissimo Monde */}
        <div style={{ marginBottom:10 }}>
          <div style={{ fontWeight:600, fontSize:12, color:'var(--or)', marginBottom:4 }}>Colissimo Monde</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            {TRANCHES_COLISSIMO_INTER.map((tr, i) => (
              <div key={i} style={{ background:'var(--bg)', borderRadius:6, padding:'4px 10px', fontSize:12 }}>
                {tr.label} → <strong>B : {TARIFS_EXP_DEFAULT.colissimo.mondeB[i]} € / C : {TARIFS_EXP_DEFAULT.colissimo.mondeC[i]} €</strong>
              </div>
            ))}
          </div>
        </div>

        {/* Chronopost France */}
        <div style={{ marginBottom:10 }}>
          <div style={{ fontWeight:600, fontSize:12, color:'var(--bl)', marginBottom:4 }}>Chronopost Chrono 13 (France domicile)</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            {TRANCHES_CHRONOPOST.map((tr, i) => (
              <div key={i} style={{ background:'var(--bg)', borderRadius:6, padding:'4px 10px', fontSize:12 }}>
                {tr.label} → <strong>{TARIFS_EXP_DEFAULT.chronopost.france[i]} €</strong>
              </div>
            ))}
          </div>
        </div>

        {/* Chronopost Express international — zones clés */}
        <div>
          <div style={{ fontWeight:600, fontSize:12, color:'var(--bl)', marginBottom:4 }}>Chronopost Express International (0–1 kg)</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            {['z1','z2','z3','z4','z5','z6','z7','z8','z9'].map(z => (
              <div key={z} style={{ background:'var(--bg)', borderRadius:6, padding:'4px 10px', fontSize:12 }}>
                {z.toUpperCase()} → <strong>{TARIFS_EXP_DEFAULT.chronopost[z][0]} €</strong>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
        {saved && <span style={{ color:'var(--ok)', fontSize:13, fontWeight:700 }}>✓ Enregistré</span>}
        <button onClick={save} className="btn bor">💾 Enregistrer</button>
      </div>
    </div>
  );
}

function OTDevisExp({ objet, updateObj, tarifsExp, biens }) {
  const t = tarifsExp || TARIFS_EXP_DEFAULT;
  const [poids,   setPoids]   = useState(objet.poids    || '');
  const [pays,    setPays]    = useState(objet.expPays   || '');
  const [transp,  setTransp]  = useState(objet.expTransp || 'colissimo');
  // Mode manuel si l'utilisateur choisit "Autre pays"
  const [zoneManuelle, setZoneManuelle] = useState(objet.expZoneManuelle || '');

  const paysDef  = PAYS_ZONES.find(p => p.label === pays);
  const isAutre  = paysDef?.colissimo === null;

  // Résolution de zone selon transporteur
  const zone = isAutre
    ? (zoneManuelle || null)
    : (transp === 'colissimo' ? paysDef?.colissimo : paysDef?.chronopost) || null;

  const devis = (poids && zone) ? calcDevis(Number(poids), zone, transp, t) : null;

  const enregistrer = () => {
    const token = objet.expToken || genExpToken();
    updateObj(objet.id, {
      poids: Number(poids),
      expPays: pays,
      expTransp: transp,
      expZone: zone,
      expZoneManuelle: zoneManuelle,
      expToken: token,
    });
  };

  const simulUrl = objet.expToken
    ? `https://app.conciergerie-alsace.com/exp/${objet.expToken}`
    : null;

  const copierAdresse = () => {
    // Chercher le locataire depuis nomLocataire stocké sur l'objet
    const nom = objet.nomLocataire || '';
    const adresse = [nom, pays].filter(Boolean).join('\n');
    navigator.clipboard.writeText(adresse).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const [sending, setSending] = useState(false);
  const [sendOk,  setSendOk]  = useState(false);
  const [sendErr, setSendErr] = useState(null);
  const [emailDest, setEmailDest] = useState(objet.emailLocataire || '');
  const [copied, setCopied] = useState(false);

  const envoyerEmail = async () => {
    if (!emailDest || !simulUrl) return;
    setSending(true); setSendErr(null);
    try {
      const bien = biens?.find(b => b.id === objet.bid);
      await envoyerEmailOT({
        toEmail: emailDest,
        toName: objet.nomLocataire || '',
        objet,
        bien: bien?.name || bien?.nom || '',
        simulUrl,
      });
      setSendOk(true);
      updateObj(objet.id, { emailLocataire: emailDest, emailOTEnvoye: true, dateEmailOT: new Date().toISOString().slice(0,10) });
      setTimeout(() => setSendOk(false), 4000);
    } catch(e) {
      setSendErr(e.message || 'Erreur envoi');
    } finally {
      setSending(false);
    }
  };

  const serviceLabel = transp === 'colissimo'
    ? (COLISSIMO_LABELS[zone] || 'Colissimo International')
    : (CHRONOPOST_LABELS[zone] || 'Chronopost Express International');

  const imprimerDevis = () => {
    if (!devis || devis.overMax) return;
    const bien = biens?.find(b => b.id === objet.bid);
    const lines = [
      'LA CONCIERGERIE',
      "Devis — Frais de traitement et d'expédition",
      '',
      `Référence : ${objet.ref || '—'}`,
      `Objet : ${objet.desc || '—'}`,
      `Appartement : ${bien?.name || '—'}`,
      `Date de découverte : ${objet.date || '—'}`,
      '',
      '─────────────────────────────────────',
      `Poids conditionné : ${poids} g  (tranche : ${devis.tranche})`,
      `Destination : ${pays || '—'}`,
      `Service : ${serviceLabel}`,
      '',
      `Frais de traitement et d'expédition HT : ${devis.prixHT} €`,
      `TVA 20 % : ${(devis.prixTTC - devis.prixHT).toFixed(2)} €`,
      '─────────────────────────────────────',
      `TOTAL TTC : ${devis.prixTTC} €`,
      '',
      "Devis valable 30 jours à compter de la date d'émission.",
      "Frais d'emballage inclus.",
      '',
      'La Conciergerie — 10 rue de la porte neuve, 68000 Colmar',
      'contact@conciergerie-alsace.com',
    ];
    const w = window.open('', '_blank', 'width=520,height=680');
    w.document.write('<pre style="font-family:monospace;font-size:13px;padding:28px;line-height:1.7">' + lines.join('\n') + '</pre>');
    w.document.close();
    w.print();
  };

  const modeLbl = { pct:`${t.margePct??25}%`, fixe:`+${t.margeFixe??0} € fixe`, cumul:`${t.margePct??25}% + ${t.margeFixe??0} € fixe` };

  return (
    <div style={{ background:'var(--cr)', borderRadius:10, padding:'12px 14px', marginTop:10 }}>
      <div style={{ fontWeight:700, fontSize:13, marginBottom:10 }}>📬 Devis expédition</div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
        {/* Poids */}
        <div>
          <label className="fl">Poids conditionné (g) *</label>
          <input type="number" min="1" value={poids} onChange={e => setPoids(e.target.value)}
            placeholder="ex : 850" style={{ width:'100%' }} />
        </div>
        {/* Service */}
        <div>
          <label className="fl">Service</label>
          <select value={transp} onChange={e => setTransp(e.target.value)} style={{ width:'100%' }}>
            <option value="colissimo">Colissimo — standard</option>
            <option value="chronopost">Chronopost — express</option>
          </select>
        </div>
      </div>

      {/* Sélecteur pays */}
      <div style={{ marginBottom:8 }}>
        <label className="fl">Pays de destination</label>
        <select value={pays} onChange={e => { setPays(e.target.value); setZoneManuelle(''); }}
          style={{ width:'100%' }}>
          <option value="">— Choisir un pays —</option>
          {PAYS_ZONES.map(p => (
            <option key={p.label} value={p.label}
              disabled={transp === 'colissimo' ? !p.colissimo && p.colissimo !== null : !p.chronopost && p.chronopost !== null}>
              {p.label}
            </option>
          ))}
        </select>
      </div>

      {/* Saisie manuelle de zone si "Autre pays" */}
      {isAutre && (
        <div style={{ marginBottom:8 }}>
          <label className="fl">
            Zone {transp === 'colissimo' ? '(france / europe / outremer1 / outremer2 / mondeB / mondeC)' : '(z1 à z9)'}
          </label>
          <input type="text" value={zoneManuelle} onChange={e => setZoneManuelle(e.target.value.toLowerCase())}
            placeholder={transp === 'colissimo' ? 'ex: mondeB' : 'ex: z4'}
            style={{ width:'100%' }} />
        </div>
      )}

      {/* Résultat devis */}
      {devis && !devis.overMax && (
        <div style={{ background:'var(--bg)', borderRadius:8, padding:'10px 14px', marginBottom:10, fontSize:13 }}>
          <div style={{ display:'flex', gap:16, flexWrap:'wrap', alignItems:'center' }}>
            <span style={{ color:'var(--si)', fontSize:11 }}>Tranche : {devis.tranche}</span>
            <span style={{ color:'var(--si)', fontSize:11 }}>Coût transp. : {devis.cout} € <em>(interne)</em></span>
            <span>HT : <strong>{devis.prixHT} €</strong></span>
            <span style={{ fontSize:15, fontWeight:700, color:'var(--or)' }}>→ Client TTC : {devis.prixTTC} €</span>
          </div>
          <div style={{ fontSize:11, color:'var(--si)', marginTop:4 }}>
            {serviceLabel} · Prestation : {modeLbl[t.margeMode||'pct']}
          </div>
        </div>
      )}
      {devis?.overMax && (
        <div style={{ background:'#fff3cd', borderRadius:8, padding:'10px 14px', marginBottom:10, fontSize:13, color:'#856404' }}>
          ⚠️ Poids supérieur à 10 kg — tarif sur devis. Contacter La Poste directement.
        </div>
      )}
      {poids && pays && !zone && !isAutre && (
        <div style={{ fontSize:12, color:'var(--er)', marginBottom:8 }}>
          ⚠️ Ce pays n'est pas desservi par {transp === 'colissimo' ? 'Colissimo' : 'Chronopost'} — essayer l'autre service.
        </div>
      )}

      {/* Boutons actions */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom: simulUrl ? 10 : 0 }}>
        <button onClick={enregistrer} disabled={!poids || !pays} className="btn bgh sm">💾 Enregistrer</button>
        {devis && !devis.overMax && (
          <button onClick={imprimerDevis} className="btn sm"
            style={{ background:'var(--or)', color:'white', border:'none' }}>🖨 Devis client</button>
        )}
        {pays && (
          <button onClick={copierAdresse} className="btn sm bor">
            {copied ? '✓ Copié !' : '📋 Copier adresse'}
          </button>
        )}
      </div>

      {/* Lien simulateur + envoi email */}
      {simulUrl && (
        <div style={{ background:'var(--bg)', borderRadius:8, padding:'10px 14px', marginTop:4 }}>
          <div style={{ fontSize:12, color:'var(--si)', marginBottom:6 }}>
            🔗 Lien simulateur client :
            <a href={simulUrl} target="_blank" rel="noopener noreferrer"
              style={{ marginLeft:6, color:'var(--or)', wordBreak:'break-all' }}>
              {simulUrl}
            </a>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
            <input type="email" value={emailDest}
              onChange={e => setEmailDest(e.target.value)}
              placeholder="Email du locataire"
              style={{ flex:1, minWidth:180, fontSize:12, padding:'5px 8px', borderRadius:6,
                border:'1.5px solid var(--border-c)' }} />
            <button onClick={envoyerEmail}
              disabled={!emailDest || sending || !simulUrl || !objet.poids}
              title={!objet.poids ? `Renseignez d'abord le poids conditionné` : ''}
              className="btn sm"
              style={{ background: objet.poids ? '#1976d2' : '#aaa', color:'white', border:'none', whiteSpace:'nowrap' }}>
              {sending ? '⏳ Envoi…' : objet.emailOTEnvoye ? '✉️ Renvoyer' : '✉️ Envoyer'}
            </button>
          </div>
          {!objet.poids && <div style={{ fontSize:12, color:'var(--or)', marginTop:4 }}>
            ⚠️ Renseignez le poids conditionné avant d'envoyer — le client en aura besoin pour le simulateur.
          </div>}
          {sendOk && <div style={{ fontSize:12, color:'var(--ok)', marginTop:4 }}>✓ Email envoyé</div>}
          {sendErr && <div style={{ fontSize:12, color:'var(--er)', marginTop:4 }}>⚠️ {sendErr}</div>}
          {objet.emailOTEnvoye && <div style={{ fontSize:11, color:'var(--si)', marginTop:4 }}>
            Dernier envoi : {objet.dateEmailOT || '—'}
          </div>}
        </div>
      )}
      {!simulUrl && poids && pays && (
        <div style={{ fontSize:12, color:'var(--si)', marginTop:6 }}>
          💡 Enregistrez d'abord pour générer le lien simulateur client.
        </div>
      )}
    </div>
  );
}


// ══════════════ OBJETS TROUVÉS — REGISTRE ADMIN ══════════════
// Gestion des 5 types avec cycles de vie légaux distincts

// Retourne les statuts actifs (hors terminaux) pour les filtres rapides
const OT_STATUTS_ACTIFS=['coffre','attente_retour','bac','stored','claimed'];
const OT_STATUTS_ALL=Object.keys({coffre:1,attente_retour:1,bac:1,stored:1,claimed:1,restitue:1,ecoule:1,depot_commissariat:1,detruit:1,jete:1,conserve_salarie:1,kit_accueil:1,shipped:1,abandoned:1,donne:1});

function OTAlertBanners({objets,otBacs}){
  const today=new Date();today.setHours(0,0,0,0);
  // Type 2 : délai 1 mois dépassé
  const retards=objets.filter(o=>o.type===2&&o.statut==='attente_retour'&&o.dateLimiteRecup&&new Date(o.dateLimiteRecup)<today);
  // Type 4 : bac ouvert depuis > 6 mois
  const vieuxBacs=(otBacs||[]).filter(b=>b.statut==='ouvert'&&((today-new Date(b.dateOuverture))/(86400000*30))>6);
  // Type 1 : au coffre depuis > 3 ans
  const coffresVieux=objets.filter(o=>o.type===1&&o.statut==='coffre'&&o.date&&((today-new Date(o.date))/(86400000*365))>3);
  if(!retards.length&&!vieuxBacs.length&&!coffresVieux.length)return null;
  return(<div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:16}}>
    {retards.length>0&&<div style={{background:'#ffebee',border:'1.5px solid #ef9a9a',borderRadius:10,padding:'10px 14px',fontSize:13}}>
      <strong style={{color:'#c62828'}}>⚠ {retards.length} objet{retards.length>1?'s':''} Type 2 — délai 1 mois dépassé</strong>
      <div style={{color:'#b71c1c',marginTop:4,fontSize:12}}>{retards.map(o=>`${o.desc} (${o.ref||'—'})`).join(' · ')}</div>
    </div>}
    {vieuxBacs.length>0&&<div style={{background:'#fff3e0',border:'1.5px solid #ffb74d',borderRadius:10,padding:'10px 14px',fontSize:13}}>
      <strong style={{color:'#e65100'}}>📦 Bac ouvert depuis plus de 6 mois</strong>
      <div style={{color:'#bf360c',marginTop:4,fontSize:12}}>Penser à faire le don (Espoir / Emmaüs)</div>
    </div>}
    {coffresVieux.length>0&&<div style={{background:'#ede7ff',border:'1.5px solid #b39ddb',borderRadius:10,padding:'10px 14px',fontSize:13}}>
      <strong style={{color:'#4527a0'}}>🔒 {coffresVieux.length} objet{coffresVieux.length>1?'s':''} au coffre depuis plus de 3 ans sans réclamation</strong>
    </div>}
  </div>);
}

function OTBacSection({otBacs,setOtBacs,objets,setObjets}){
  const[showDonModal,setShowDonModal]=useState(false);
  const[donAssoc,setDonAssoc]=useState('Espoir');
  const[donDate,setDonDate]=useState(today());
  const bacOuvert=(otBacs||[]).find(b=>b.statut==='ouvert');
  const nbDansBac=bacOuvert?(objets||[]).filter(o=>o.bacId===bacOuvert.id&&o.statut==='bac').length:0;
  const faireDon=()=>{
    if(!bacOuvert)return;
    const updatedBacs=(otBacs||[]).map(b=>b.id===bacOuvert.id?{...b,statut:'donne',dateDon:donDate,assoc:donAssoc}:b);
    const newBac={id:'bac'+Date.now(),dateOuverture:today(),statut:'ouvert'};
    setOtBacs([...updatedBacs,newBac]);
    setObjets(prev=>prev.map(o=>o.bacId===bacOuvert.id?{...o,statut:'donne'}:o));
    setShowDonModal(false);
  };
  return(<div style={{background:'#fff3e0',borderRadius:10,padding:'12px 14px',marginBottom:16,border:'1px solid #ffcc80'}}>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:8}}>
      <div>
        <span style={{fontWeight:700,fontSize:13}}>📦 Bac en cours</span>
        {bacOuvert&&<span style={{fontSize:12,color:'var(--si)',marginLeft:8}}>ouvert le {fd(bacOuvert.dateOuverture)}</span>}
        <span style={{marginLeft:10,background:'#ff9800',color:'white',fontSize:11,fontWeight:700,padding:'2px 8px',borderRadius:999}}>{nbDansBac} objet{nbDansBac>1?'s':''}</span>
      </div>
      {bacOuvert&&<button onClick={()=>setShowDonModal(true)} className="btn sm" style={{background:'#ff6f00',color:'white',border:'none'}}>🤝 Faire le don</button>}
    </div>
    {showDonModal&&<M onC={()=>setShowDonModal(false)} title="Don du bac" col="#ff6f00" ch={<div style={{display:'flex',flexDirection:'column',gap:12}}>
      <div><label className="fl">Association</label>
        <select value={donAssoc} onChange={e=>setDonAssoc(e.target.value)}>
          <option>Espoir</option><option>Emmaüs</option>
        </select>
      </div>
      <div><label className="fl">Date du don</label><input type="date" value={donDate} onChange={e=>setDonDate(e.target.value)}/></div>
      <div style={{fontSize:12,color:'var(--si)'}}>Cette action clôture le bac actuel ({nbDansBac} objet{nbDansBac>1?'s':''}) et en ouvre un nouveau.</div>
      <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
        <button onClick={()=>setShowDonModal(false)} className="btn bgh">Annuler</button>
        <button onClick={faireDon} className="btn" style={{background:'#ff6f00',color:'white',border:'none'}}>✓ Confirmer le don</button>
      </div>
    </div>}/>}
  </div>);
}

function OTBordereauCommissariat({objet,bien}){
  const print=()=>window.print();
  return(<div id="ot-bordereau" style={{fontFamily:'serif',fontSize:13,lineHeight:1.6,padding:'20px 24px',border:'2px solid #333',borderRadius:8,background:'white',color:'#111'}}>
    <div style={{textAlign:'center',borderBottom:'1px solid #aaa',paddingBottom:12,marginBottom:12}}>
      <div style={{fontSize:16,fontWeight:700}}>LA CONCIERGERIE</div>
      <div style={{fontSize:11,color:'#555'}}>Bordereau de dépôt — pièce d&apos;identité / moyen de paiement trouvé</div>
    </div>
    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px 20px',marginBottom:12}}>
      <div><strong>Référence :</strong> {objet.ref||'—'}</div>
      <div><strong>Date de découverte :</strong> {fd(objet.date)}</div>
      <div><strong>Type :</strong> {objet.sousType==='identite'?'Pièce d\'identité':'Moyen de paiement'}</div>
      <div><strong>Appartement :</strong> {bien?.name||'—'}</div>
      <div style={{gridColumn:'1/-1'}}><strong>Description :</strong> {objet.desc}</div>
      {objet.nomLocataire&&<div style={{gridColumn:'1/-1'}}><strong>Client présumé :</strong> {objet.nomLocataire}</div>}
      <div><strong>Date limite récupération :</strong> {fd(objet.dateLimiteRecup)}</div>
      <div><strong>Notifications effectuées :</strong> {objet.notifie?`Oui (${fd(objet.dateNotification)})`:'Non'}</div>
    </div>
    <div style={{borderTop:'1px solid #aaa',paddingTop:10,marginTop:10,fontSize:11,color:'#555'}}>
      Conformément à la réglementation, cet objet n&apos;ayant pas été réclamé dans le délai d&apos;un mois, il est remis aux autorités compétentes ce jour.
    </div>
    <div style={{marginTop:16,display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
      <div style={{borderTop:'1px solid #333',paddingTop:6,fontSize:11,color:'#444',textAlign:'center'}}>Signature remettant<br/>La Conciergerie</div>
      <div style={{borderTop:'1px solid #333',paddingTop:6,fontSize:11,color:'#444',textAlign:'center'}}>N° récépissé commissariat<br/><input type="text" placeholder="À compléter" style={{border:'none',borderBottom:'1px solid #333',width:'100%',textAlign:'center',fontSize:12,background:'transparent'}}/></div>
    </div>
    <div style={{marginTop:16,display:'flex',justifyContent:'flex-end'}}>
      <button onClick={print} className="btn sm" style={{background:'#1565c0',color:'white',border:'none'}}>🖨 Imprimer</button>
    </div>
  </div>);
}

function SecObjets({objets,setObjets,biens,clients,sejours,otBacs,setOtBacs,tarifsExp}){
  const[filterType,setFilterType]=useState('all');
  const[filterStatut,setFilterStatut]=useState('actifs');
  const[search,setSearch]=useState('');
  const[edit,setEdit]=useState(null);
  const[showAdd,setShowAdd]=useState(false);
  const[showBordereau,setShowBordereau]=useState(null);

  // Filtre actifs = exclut les statuts terminaux
  const TERMINAUX=['jete','conserve_salarie','kit_accueil','donne','ecoule','detruit','shipped','abandoned'];
  const filtered=(objets||[]).filter(o=>{
    const matchT=filterType==='all'||String(o.type)===String(filterType);
    const matchS=filterStatut==='all'||(filterStatut==='actifs'&&!TERMINAUX.includes(o.statut))||(filterStatut==='archives'&&TERMINAUX.includes(o.statut))||(o.statut===filterStatut);
    const matchQ=!search||o.desc?.toLowerCase().includes(search.toLowerCase())||o.ref?.toLowerCase().includes(search.toLowerCase());
    return matchT&&matchS&&matchQ;
  });

  // KPIs types actifs
  const countByType=[1,2,4].map(t=>({t,n:(objets||[]).filter(o=>String(o.type)===String(t)&&!TERMINAUX.includes(o.statut)).length}));

  const updateObj=(id,patch)=>setObjets(prev=>prev.map(o=>o.id===id?{...o,...patch}:o));

  const typeLabel={1:'🔒 Valeur',2:'🪪 Identité/Paiement',4:'📦 Divers'};
  const typeColor={1:'#7c4dff',2:'#1565c0',4:'#e65100'};

  return(<div>
    <OTAlertBanners objets={objets||[]} otBacs={otBacs||[]}/>
    <OTBacSection otBacs={otBacs||[]} setOtBacs={setOtBacs} objets={objets||[]} setObjets={setObjets}/>

    {/* KPIs par type */}
    <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:16}}>
      {countByType.map(({t,n})=>(
        <div key={t} onClick={()=>setFilterType(filterType===t?'all':t)}
          style={{cursor:'pointer',padding:'8px 14px',borderRadius:10,border:`2px solid ${filterType===t?typeColor[t]:'var(--li)'}`,background:filterType===t?typeColor[t]+'18':'var(--bg)',minWidth:90,textAlign:'center'}}>
          <div style={{fontSize:12,color:typeColor[t],fontWeight:700}}>{typeLabel[t]}</div>
          <div style={{fontSize:22,fontWeight:700,color:typeColor[t]}}>{n}</div>
        </div>
      ))}
      <div onClick={()=>setFilterType('all')} style={{cursor:'pointer',padding:'8px 14px',borderRadius:10,border:`2px solid ${filterType==='all'?'var(--or)':'var(--li)'}`,background:filterType==='all'?'#fff4d6':'var(--bg)',minWidth:90,textAlign:'center'}}>
        <div style={{fontSize:12,color:'var(--or)',fontWeight:700}}>Tous types</div>
        <div style={{fontSize:22,fontWeight:700,color:'var(--or)'}}>{(objets||[]).filter(o=>!TERMINAUX.includes(o.statut)).length}</div>
      </div>
    </div>

    {/* Filtres statut + recherche + bouton */}
    <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap'}}>
      <select value={filterStatut} onChange={e=>setFilterStatut(e.target.value)} style={{flex:'0 0 auto'}}>
        <option value="actifs">Objets actifs</option>
        <option value="all">Tous les statuts</option>
        <option value="archives">Archivés</option>
        <option value="coffre">Au coffre</option>
        <option value="attente_retour">Attente retour</option>
        <option value="bac">Dans le bac</option>
      </select>
      <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Référence ou description…" style={{flex:1,minWidth:160}}/>
      <button onClick={()=>setShowAdd(true)} className="btn bor sm"><Ic n="plus"/>Déclarer</button>
    </div>

    {filtered.length===0&&<div className="empty">Aucun objet pour ce filtre.</div>}

    <div style={{display:'flex',flexDirection:'column',gap:10}}>
      {filtered.sort((a,b)=>(b.date||'').localeCompare(a.date||'')).map(o=>{
        const bien=biens.find(b=>b.id===o.bid);
        const s=OT_STATUS[o.statut]||OT_STATUS.stored;
        const tColor=typeColor[o.type]||'var(--si)';
        return(<div key={o.id} className="card" style={{padding:'14px 16px',borderLeft:`3px solid ${tColor}`}}>
          <div style={{display:'flex',justifyContent:'space-between',flexWrap:'wrap',gap:10}}>
            <div style={{flex:1}}>
              <div style={{display:'flex',gap:7,alignItems:'center',marginBottom:6,flexWrap:'wrap'}}>
                {o.ref&&<span style={{fontFamily:'Fredoka,cursive',fontSize:15,fontWeight:700,color:'var(--or)'}}>{o.ref}</span>}
                <span style={{background:s.bg,color:s.c,fontSize:11,fontWeight:700,padding:'3px 9px',borderRadius:999}}>{s.l}</span>
                <span style={{fontSize:11,background:tColor+'22',color:tColor,padding:'2px 7px',borderRadius:999,fontWeight:700}}>{typeLabel[o.type]||'—'}</span>
                {o.casier&&(o.statut==='stored'||o.statut==='claimed')&&<span style={{background:'var(--cr)',fontSize:11,fontWeight:700,padding:'3px 8px',borderRadius:999}}>📦 Casier {o.casier}</span>}
                {o.type===1&&o.statut==='coffre'&&<span style={{background:'#ede7ff',color:'#7c4dff',fontSize:11,fontWeight:700,padding:'3px 8px',borderRadius:999}}>🔒 Coffre-fort</span>}
              </div>
              <div style={{fontWeight:700,fontSize:13,marginBottom:3}}>{o.desc}</div>
              <div style={{fontSize:11,color:'var(--si)',display:'flex',gap:12,flexWrap:'wrap'}}>
                <span>🏠 {bien?.name||'—'}</span>
                <span>📅 {fd(o.date)}</span>
                {o.nomLocataire&&<span>👤 {o.nomLocataire}</span>}
                {o.type===2&&o.dateLimiteRecup&&<span style={{color:new Date(o.dateLimiteRecup)<new Date()?'var(--dg)':'var(--or)',fontWeight:700}}>⏳ Limite : {fd(o.dateLimiteRecup)}</span>}
                {o.type===1&&o.statut==='coffre'&&<span style={{color:'var(--si)'}}>Conservation 3 ans (art. 2276 Cc)</span>}
              </div>
              {o.notes&&<div style={{fontSize:11,color:'var(--si)',marginTop:4,fontStyle:'italic'}}>{o.notes}</div>}
              {/* Lien séjour Types 1 & 2 */}
              {(o.type===1||o.type===2)&&(o.nomLocataire||o.sejourId)&&(
                <div style={{marginTop:6,padding:'6px 10px',background:'#e8f4fd',borderRadius:8,fontSize:12,display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
                  <span>📨 Client à prévenir : <strong>{o.nomLocataire||o.sejourId}</strong></span>
                  {!o.notifie&&<button onClick={()=>updateObj(o.id,{notifie:true,dateNotification:today()})} className="btn sm" style={{background:'#1976d2',color:'white',border:'none',padding:'3px 10px'}}>✓ Notifié</button>}
                  {o.notifie&&<span style={{color:'var(--ok)',fontWeight:700}}>✓ Notifié le {fd(o.dateNotification)}</span>}
                </div>
              )}
            </div>
            {o.photo&&<img src={o.photo} style={{width:70,height:70,objectFit:'cover',borderRadius:10,flexShrink:0}} alt="objet"/>}
          </div>

          {/* Devis expédition (Types 1 et 4) */}
          {(o.type===1||o.type===4)&&<OTDevisExp objet={o} updateObj={updateObj} tarifsExp={tarifsExp} biens={biens}/>}
          {/* Actions contextuelles */}
          <div style={{display:'flex',gap:6,marginTop:10,flexWrap:'wrap'}}>
            {/* Type 1 — coffre */}
            {o.type===1&&o.statut==='coffre'&&<>
              <button onClick={()=>updateObj(o.id,{statut:'restitue',dateRestitution:today()})} className="btn bgh sm">✅ Restituer</button>
              <button onClick={()=>updateObj(o.id,{statut:'ecoule'})} className="btn bgh sm" style={{color:'var(--si)'}}>🗑 Écouler (3 ans)</button>
            </>}
            {o.type===1&&o.statut==='restitue'&&<span style={{color:'var(--ok)',fontSize:12}}>✓ Restitué le {fd(o.dateRestitution)}</span>}

            {/* Type 2 — attente_retour */}
            {o.type===2&&o.statut==='attente_retour'&&<>
              <button onClick={()=>updateObj(o.id,{statut:'restitue',dateRestitution:today()})} className="btn bgh sm">✅ Restitué</button>
              {o.sousType==='identite'&&<button onClick={()=>setEdit({...o,_action:'commissariat'})} className="btn sm" style={{background:'#1565c0',color:'white',border:'none'}}>🚔 Déposer commissariat</button>}
              {o.sousType==='paiement'&&<button onClick={()=>updateObj(o.id,{statut:'detruit',dateDestruction:today()})} className="btn bgh sm" style={{color:'var(--dg)'}}>🗑 Détruire</button>}
            </>}
            {o.type===2&&o.statut==='depot_commissariat'&&<>
              <button onClick={()=>setShowBordereau(o)} className="btn sm" style={{background:'#1565c0',color:'white',border:'none'}}>🖨 Bordereau</button>
              <span style={{fontSize:12,color:'#1565c0',alignSelf:'center'}}>Déposé le {fd(o.dateDepot)} — réf. {o.refDepot||'—'}</span>
            </>}

            {/* Type 4 — bac */}
            {o.type===4&&o.statut==='bac'&&<span style={{fontSize:11,color:'var(--si)',alignSelf:'center'}}>Dans le bac — don groupé à la clôture</span>}

            {/* Anciens statuts hérités */}
            {o.statut==='stored'&&!o.type&&<>
              <button onClick={()=>setEdit({...o,_action:'claim'})} className="btn bgh sm">📋 Réclamé</button>
              <button onClick={()=>updateObj(o.id,{statut:'abandoned'})} className="btn bgh sm" style={{color:'var(--si)'}}>🗑 Archiver</button>
            </>}
            {o.statut==='claimed'&&!o.type&&<>
              <button onClick={()=>setEdit({...o,_action:'ship'})} className="btn bmn sm">📬 Expédié</button>
              <button onClick={()=>updateObj(o.id,{statut:'stored'})} className="btn bgh sm">↺ En stock</button>
            </>}
          </div>
        </div>);
      })}
    </div>

    {/* Modal déclaration */}
    {showAdd&&<M onC={()=>setShowAdd(false)} title="📦 Déclarer un objet trouvé" col="var(--or)"
      ch={<OTQuick biens={biens} objets={objets} setObjets={setObjets} otBacs={otBacs} setOtBacs={setOtBacs} sejours={sejours||[]} onClose={()=>setShowAdd(false)}/>}/>}

    {/* Modal commissariat */}
    {edit&&edit._action==='commissariat'&&<M onC={()=>setEdit(null)} title="Dépôt commissariat" col="#1565c0" ch={<div style={{display:'flex',flexDirection:'column',gap:12}}>
      <div style={{fontSize:13,marginBottom:4}}>Pièce d&apos;identité — <strong>{edit.desc}</strong></div>
      <div><label className="fl">Date de dépôt</label><input type="date" id="ot-dep-date" defaultValue={today()}/></div>
      <div><label className="fl">N° récépissé (optionnel)</label><input id="ot-dep-ref" placeholder="ex : 2026-1234"/></div>
      <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
        <button onClick={()=>setEdit(null)} className="btn bgh">Annuler</button>
        <button onClick={()=>{
          const dateDepot=document.getElementById('ot-dep-date').value;
          const refDepot=document.getElementById('ot-dep-ref').value;
          updateObj(edit.id,{statut:'depot_commissariat',dateDepot,refDepot});
          setEdit(null);
          setShowBordereau({...edit,statut:'depot_commissariat',dateDepot,refDepot});
        }} className="btn" style={{background:'#1565c0',color:'white',border:'none'}}>🚔 Confirmer</button>
      </div>
    </div>}/>}

    {/* Bordereau commissariat */}
    {showBordereau&&<M onC={()=>setShowBordereau(null)} title="Bordereau commissariat" col="#1565c0"
      ch={<OTBordereauCommissariat objet={showBordereau} bien={biens.find(b=>b.id===showBordereau.bid)}/>}/>}

    {/* Modals anciens statuts hérités */}
    {edit&&edit._action==='claim'&&<M onC={()=>setEdit(null)} title="Réclamation client" col="var(--or)" ch={<div style={{display:'flex',flexDirection:'column',gap:12}}>
      <div><label className="fl">Nom du client réclamant</label><input id="cn" defaultValue={edit.claimName||''} placeholder="Marie Dupont"/></div>
      <div><label className="fl">Tarif expédition proposé (€)</label><input type="number" id="sp" defaultValue={edit.shippingPrice||''} style={{maxWidth:160}}/></div>
      <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
        <button onClick={()=>setEdit(null)} className="btn bgh">Annuler</button>
        <button onClick={()=>{updateObj(edit.id,{statut:'claimed',claimName:document.getElementById('cn').value,shippingPrice:document.getElementById('sp').value});setEdit(null);}} className="btn bor">Confirmer</button>
      </div>
    </div>}/>}
    {edit&&edit._action==='ship'&&<M onC={()=>setEdit(null)} title="Confirmer l'expédition" col="var(--mn)" ch={<div style={{display:'flex',flexDirection:'column',gap:12}}>
      <div style={{fontSize:13}}>Confirmer l&apos;expédition de <strong>{edit.desc}</strong> ?</div>
      <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
        <button onClick={()=>setEdit(null)} className="btn bgh">Annuler</button>
        <button onClick={()=>{updateObj(edit.id,{statut:'shipped',shippedAt:today(),casier:null});setEdit(null);}} className="btn bmn">✓ Expédié</button>
      </div>
    </div>}/>}
  </div>);
}

// → OTQuick déplacé dans src/components/OTQuick.jsx (refacto étape 2)


// → LeaveForm, CommentaireModal déplacés dans src/components/RHTerrain.jsx (refacto étape D)

// → AlerteSanteButton, CaniBandeau, CaniHydrationBanner, CaniTab déplacés dans src/components/Canicule.jsx (refacto étape E)
// ══════════════ Helper rapport : génère la structure à afficher selon le modèle du bien ══════════════
// Renvoie {items, photoItems, genPhotos, sections, useNew}
// - useNew=true  → utilise bien.mr (nouveau modèle pièce par pièce)
// - useNew=false → utilise l'ancien modèle CL + bien.ec (rétrocompatibilité)
// → getRapportStructure, ErrorBoundary, RapportModal déplacés dans src/components/ErrorBoundary.jsx

// → SimpleChat déplacé dans src/components/SimpleChat.jsx (refacto étape 1)

// ══════════════ INTERFACES SALARIÉ / PRESTA / CLIENT ══════════════
// → RepLibreSelector, ControleLibreSelector déplacés dans src/components/RapportSelectors.jsx (refacto étape C)

// → GerantUI déplacé dans src/components/GerantUI.jsx (refacto étape GerantUI)


// → StaffUI déplacé dans src/components/StaffUI.jsx (refacto étape StaffUI)
// → ProvUI déplacé dans src/components/ProvUI.jsx (refacto étape 3)
// ══════════════ ONBOARDING PREMIÈRE CONNEXION PROPRIÉTAIRE ══════════════
// ══════════════ ÉCRAN DE DÉFINITION DE MOT DE PASSE (retour d'email) ══════════════
// Affiché quand l'utilisateur arrive via le lien « Définir mon mot de passe » reçu
// par email (RECOVERY_MODE détecté au chargement). Le jeton recovery fait office
// d'authentification : on ne demande donc PAS d'ancien mot de passe, juste le nouveau.
// Réutilise PUT /auth/v1/user, comme ClientOnboarding et ChangePasswordModal.
function RecoveryScreen(){
  const[newPwd,setNewPwd]=React.useState('');
  const[confirmPwd,setConfirmPwd]=React.useState('');
  const[err,setErr]=React.useState('');
  const[ok,setOk]=React.useState(false);
  const[loading,setLoading]=React.useState(false);
  const[showNew,setShowNew]=React.useState(false);

  const handleSetPassword=async()=>{
    setErr('');
    if(!newPwd.trim()||!confirmPwd.trim()){setErr('Veuillez remplir les deux champs.');return;}
    if(newPwd.length<8){setErr('Le mot de passe doit faire au moins 8 caractères.');return;}
    if(newPwd!==confirmPwd){setErr('Les deux mots de passe ne correspondent pas.');return;}
    setLoading(true);
    try{
      const updateRes=await fetch(`${SUPA_URL}/auth/v1/user`,{
        method:'PUT',
        headers:{'Content-Type':'application/json','apikey':SUPA_KEY,'Authorization':`Bearer ${SUPA_TOKEN}`},
        body:JSON.stringify({password:newPwd})
      });
      if(!updateRes.ok){
        const d=await updateRes.json();
        setErr(d?.msg||d?.message||'Lien expiré ou invalide. Demandez un nouveau lien à votre gestionnaire.');
        setLoading(false);return;
      }
      setOk(true);
    }catch(e){setErr('Erreur réseau. Réessayez.');setLoading(false);}
  };

  return(
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',padding:16,background:'var(--bg)'}}>
      <div style={{maxWidth:420,width:'100%'}}>
        <div style={{textAlign:'center',marginBottom:24}}>
          <div className="D" style={{fontSize:'2rem',color:'var(--ink)',lineHeight:1}}>La Conciergerie</div>
        </div>
        <div style={{background:'linear-gradient(135deg,#ffe8e8,var(--wh))',borderLeft:'4px solid var(--pk)',borderRadius:12,padding:'20px 22px',marginBottom:24}}>
          <div className="D" style={{fontSize:22,marginBottom:6}}>Bienvenue 👋</div>
          <div style={{fontSize:13,color:'var(--si)',lineHeight:1.6}}>
            Pour activer votre accès, choisissez votre mot de passe personnel ci-dessous.
          </div>
        </div>
        {ok?(
          <div style={{background:'#e0fbe0',border:'1px solid #86efac',borderRadius:12,padding:'18px 20px',textAlign:'center'}}>
            <div style={{fontSize:22,marginBottom:8}}>✅</div>
            <div className="D" style={{fontSize:16,color:'#166534',marginBottom:6}}>Mot de passe créé !</div>
            <div style={{fontSize:12,color:'#166534',marginBottom:16,lineHeight:1.5}}>Votre compte est sécurisé. Connectez-vous avec votre nouveau mot de passe pour accéder à votre espace.</div>
            <button onClick={()=>{try{localStorage.removeItem('supa_session');}catch{}window.location.href=window.location.pathname;}} className="btn bor" style={{width:'100%',justifyContent:'center'}}>Se connecter →</button>
          </div>
        ):(
          <div style={{background:'var(--wh)',borderRadius:12,padding:'24px 22px',boxShadow:'0 4px 20px rgba(42,37,32,.08)',border:'1px solid var(--li)'}}>
            <div className="D" style={{fontSize:15,marginBottom:16,color:'var(--ink)'}}>🔒 Choisissez votre mot de passe</div>
            <div style={{display:'flex',flexDirection:'column',gap:14}}>
              <div>
                <label className="fl">Nouveau mot de passe <span style={{fontSize:10,color:'var(--si)'}}>(8 caractères min.)</span></label>
                <div style={{position:'relative'}}>
                  <input type={showNew?'text':'password'} value={newPwd} onChange={e=>{setNewPwd(e.target.value);setErr('');}} placeholder="Choisissez un mot de passe sécurisé" style={{paddingRight:40}} autoFocus/>
                  <button onClick={()=>setShowNew(v=>!v)} style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',fontSize:14,color:'var(--si)',padding:4}} tabIndex={-1}>{showNew?'🙈':'👁'}</button>
                </div>
              </div>
              {newPwd.length>0&&<div style={{height:4,borderRadius:2,background:'var(--li)',overflow:'hidden'}}>
                <div style={{height:'100%',borderRadius:2,transition:'width .3s,background .3s',width:`${Math.min(100,newPwd.length*8)}%`,background:newPwd.length<8?'#ef4444':newPwd.length<12?'#f59e0b':'#22c55e'}}/>
              </div>}
              <div>
                <label className="fl">Confirmer le mot de passe</label>
                <input type="password" value={confirmPwd} onChange={e=>{setConfirmPwd(e.target.value);setErr('');}} placeholder="Répétez votre mot de passe" onKeyDown={e=>{if(e.key==='Enter')handleSetPassword();}}/>
              </div>
              {err&&<div style={{background:'#fde0e0',border:'1px solid #f5b0b0',borderRadius:9,padding:'9px 13px',fontSize:12,color:'#8a1010'}}>⚠️ {err}</div>}
              <button onClick={handleSetPassword} disabled={loading} className="btn bor" style={{width:'100%',justifyContent:'center',padding:'11px 20px',fontSize:14,borderRadius:12}}>
                {loading?'Enregistrement…':'Créer mon mot de passe →'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// → ClientOnboarding, CaniculeBannerClient, MigrationBanner, ClientUI, DemandeForm, ClientDashboardPresta, ClientPlanning, GuideProprietaire, GuideStep, GuideTip déplacés dans src/components/ClientUI.jsx (refacto étape ClientUI)

// ══════════════ COMPTES UTILISATEURS ══════════════
const EMAIL_ROLES={
  'admin@laconciergerie.fr':      {type:'admin'},
  'claire-admin@laconciergerie.fr': {type:'admin'},
  'franck-admin@laconciergerie.fr': {type:'admin'},
  'claire@laconciergerie.fr':     {type:'gerant', id:'g1'},
  'franck@laconciergerie.fr':     {type:'gerant', id:'g2'},
};

function LoginScreen({onSel}){
  const[email,setEmail]=useState('');
  const[pwd,setPwd]=useState('');
  const[err,setErr]=useState('');
  const[loading,setLoading]=useState(false);
  const[showPwd,setShowPwd]=useState(false);
  // Mode « mot de passe oublié » : l'utilisateur déclenche lui-même l'envoi du mail de réinitialisation.
  const[forgotMode,setForgotMode]=useState(false);
  const[forgotMsg,setForgotMsg]=useState(''); // message neutre affiché après envoi
  const[forgotSending,setForgotSending]=useState(false);

  // Envoie le mail de réinitialisation via sendAccessEmail (même mécanisme que l'accès initial,
  // redirect_to forcé sur le domaine officiel). Message NEUTRE : on ne révèle jamais si l'email
  // existe en base, pour ne pas permettre l'énumération des comptes.
  const handleForgot=async()=>{
    if(!email.trim()){setErr('Saisis ton adresse email d\u2019abord.');return;}
    setForgotSending(true);setErr('');setForgotMsg('');
    try{await sendAccessEmail(email);}catch(e){console.warn('[forgot] envoi',e);}
    // Quel que soit le résultat (email connu ou non), on affiche le même message.
    setForgotMsg('Si un compte existe pour cette adresse, un email de réinitialisation vient d\u2019être envoyé. Pensez à vérifier vos spams.');
    setForgotSending(false);
  };

  const handleLogin=async()=>{
    if(!email.trim()||!pwd.trim()){setErr('Remplis les deux champs.');return;}
    setLoading(true);setErr('');
    try{
      const cleanEmail=email.trim().toLowerCase();
      const res=await fetch(`${SUPA_URL}/auth/v1/token?grant_type=password`,{
        method:'POST',
        headers:{'Content-Type':'application/json','apikey':SUPA_KEY},
        body:JSON.stringify({email:cleanEmail,password:pwd})
      });
      const data=await res.json();
      if(!res.ok){setErr('Email ou mot de passe incorrect.');setLoading(false);return;}
      supaSaveSession(data);

      // 1) Vérification dans la liste des comptes "système" (admin, gérants)
      let role=EMAIL_ROLES[cleanEmail];

      // 2) Si pas trouvé, on cherche dynamiquement dans Supabase
      //    (salariés, prestataires, propriétaires) par correspondance d'email
      if(!role){
        try{
          const [staffList,provList,clientList]=await Promise.all([
            supaGet('staff7'),
            supaGet('prov7'),
            supaGet('clients7'),
          ]);
          const matchEmail=p=>{
            if((p.email||'').trim().toLowerCase()===cleanEmail)return true;
            // Accès secondaires (ex. propriétaire + secrétaire) : un même profil
            // peut déclarer plusieurs emails autorisés, chacun avec son propre compte Auth.
            if(Array.isArray(p.accesSecondaires)){
              return p.accesSecondaires.some(a=>(a&&a.email||'').trim().toLowerCase()===cleanEmail);
            }
            return false;
          };

          const sal=(staffList||[]).find(matchEmail);
          if(sal){
            // Compte désactivé (départ salarié) ou date de fin de contrat dépassée → refus
            const today=new Date().toISOString().slice(0,10);
            if(sal.archived){
              setErr("Ce compte a été désactivé. Pour toute question, contactez votre gestionnaire.");
              setLoading(false);
              return;
            }
            // Étape 1B : la porte se ferme s'il existe au moins un contrat et
            // qu'AUCUN n'est actif aujourd'hui (contrat clos, ou pas encore
            // commencé). Décision explicite de Claire : une fiche SANS aucun
            // contrat (créée sans date d'embauche) laisse passer — sinon
            // oublier de saisir la date d'embauche enfermerait le salarié
            // dehors sans que personne ne comprenne pourquoi.
            const csSal=contratsOf(sal);
            if(csSal.length&&!contratActif(sal,today)){
              const dcSal=dernierContrat(sal);
              if(dcSal&&dcSal.hire>today){
                setErr(`Votre contrat débute le ${new Date(dcSal.hire).toLocaleDateString('fr-FR')}. L'accès à l'app sera ouvert à cette date.`);
              }else{
                setErr(dcSal?.endDate
                  ?`Votre contrat a pris fin le ${new Date(dcSal.endDate).toLocaleDateString('fr-FR')}. L'accès à l'app est clos.`
                  :"L'accès à l'app est clos. Pour toute question, contactez votre gestionnaire.");
              }
              setLoading(false);
              return;
            }
            role={type:'staff',id:sal.id};
          }

          if(!role){
            const pr=(provList||[]).find(matchEmail);
            if(pr){role={type:'prov',id:pr.id};}
          }

          if(!role){
            const cl=(clientList||[]).find(matchEmail);
            if(cl){role={type:'client',id:cl.id};}
          }
        }catch(lookupErr){
          console.error('Erreur lookup rôle:',lookupErr);
        }
      }

      if(!role){
        setErr("Compte non reconnu dans l'application. Contactez votre gestionnaire.");
        setLoading(false);
        return;
      }

      // Rechargement complet pour que toutes les lectures Supabase démarrent avec le token utilisateur
      try{sessionStorage.setItem('pending_role',JSON.stringify(role));}catch{}
      window.location.href=window.location.pathname;
    }catch(e){setErr('Erreur de connexion. Réessaie.');setLoading(false);}
  };

  const handleKey=e=>{if(e.key==='Enter')handleLogin();};

  return(
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',padding:22,position:'relative',overflow:'hidden',background:'var(--bg)'}}>
      <div className="blob" style={{background:'var(--pk)',width:380,height:380,top:'-90px',left:'-90px'}}/>
      <div className="blob" style={{background:'var(--ye)',width:330,height:330,bottom:'-70px',right:'-50px'}}/>
      <div className="blob" style={{background:'var(--mn)',width:280,height:280,top:'40%',right:'20%'}}/>
      <div style={{maxWidth:420,width:'100%',position:'relative',zIndex:1}}>
        {/* Logo */}
        <div style={{textAlign:'center',marginBottom:36}}>
          <div className="D" style={{fontSize:'2.8rem',color:'var(--ink)',lineHeight:1}}>La Conciergerie</div>
          <div style={{fontSize:13,color:'var(--si)',marginTop:8}}>Connexion à votre espace</div>
        </div>

        {/* Carte login */}
        <div style={{background:'var(--wh)',borderRadius:20,padding:'32px 30px',boxShadow:'0 8px 40px rgba(42,37,32,.12)',border:'1px solid var(--li)'}}>
          <div style={{display:'flex',flexDirection:'column',gap:18}}>

            {/* Email */}
            <div>
              <label className="fl">Adresse email</label>
              <div style={{position:'relative'}}>
                <span style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',fontSize:15,pointerEvents:'none'}}>✉️</span>
                <input
                  type="email"
                  placeholder="prenom@laconciergerie.fr"
                  value={email}
                  onChange={e=>{setEmail(e.target.value);setErr('');}}
                  onKeyDown={handleKey}
                  style={{paddingLeft:38}}
                  autoComplete="email"
                  autoFocus
                />
              </div>
              {email.trim().toLowerCase()==='admin@laconciergerie.fr'&&<div style={{background:'#fff4d6',border:'1px solid #f59e0b',borderRadius:9,padding:'8px 11px',fontSize:11.5,color:'#92400e',marginTop:7,lineHeight:1.5}}>⚠️ Compte partagé. Pour éviter les conflits de session, connectez-vous plutôt avec <strong>claire-admin@</strong> ou <strong>franck-admin@</strong>.</div>}
            </div>

            {/* Mot de passe */}
            <div>
              <label className="fl">Mot de passe</label>
              <div style={{position:'relative'}}>
                <span style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',fontSize:15,pointerEvents:'none'}}>🔒</span>
                <input
                  type={showPwd?'text':'password'}
                  placeholder="••••••••"
                  value={pwd}
                  onChange={e=>{setPwd(e.target.value);setErr('');}}
                  onKeyDown={handleKey}
                  style={{paddingLeft:38,paddingRight:42}}
                  autoComplete="current-password"
                />
                <button
                  onClick={()=>setShowPwd(v=>!v)}
                  style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',fontSize:14,color:'var(--si)',padding:4}}
                  tabIndex={-1}
                >{showPwd?'🙈':'👁'}</button>
              </div>
              {/* Lien « Mot de passe oublié ? » — déclenche l'envoi autonome du mail de réinitialisation */}
              <div style={{textAlign:'right',marginTop:6}}>
                <button
                  onClick={handleForgot}
                  disabled={forgotSending}
                  style={{background:'none',border:'none',padding:0,cursor:forgotSending?'default':'pointer',fontSize:11.5,color:'var(--si)',textDecoration:'underline'}}
                  tabIndex={-1}
                >{forgotSending?'Envoi…':'Mot de passe oublié ?'}</button>
              </div>
            </div>

            {/* Confirmation neutre après demande de réinitialisation */}
            {forgotMsg&&<div style={{background:'#e6f4ea',border:'1px solid #a7d7b8',borderRadius:9,padding:'9px 13px',fontSize:12,color:'#1e5631',lineHeight:1.5}}>✉️ {forgotMsg}</div>}

            {/* Erreur */}
            {err&&<div style={{background:'#fde0e0',border:'1px solid #f5b0b0',borderRadius:9,padding:'9px 13px',fontSize:12,color:'#8a1010',display:'flex',alignItems:'center',gap:8}}>⚠️ {err}</div>}

            {/* Bouton */}
            <button
              onClick={handleLogin}
              disabled={loading}
              className="btn bor"
              style={{width:'100%',justifyContent:'center',padding:'11px 20px',fontSize:14,borderRadius:12,marginTop:4}}
            >
              {loading?'Connexion…':'Se connecter →'}
            </button>
          </div>
        </div>

        {/* Aide discrète */}
        <div style={{textAlign:'center',marginTop:20,fontSize:11,color:'var(--si)'}}>
          Accès réservé aux membres de l'équipe La Conciergerie
        </div>
      </div>
    </div>
  );
}

// ══════════════ BANNIÈRE DIAGNOSTIC SYNC ══════════════
// Affiche en haut de l'app un bandeau rouge persistant si une opération Supabase
// (lecture ou écriture) a échoué récemment. Aide à diagnostiquer les problèmes de
// sync (perte de réseau, payload trop gros, RLS, etc.).
// ══════════════ BANDEAU ADMIN — RAPPORTS DE MÉNAGE NON SOUMIS DEPUIS 15+ min ══════════════
// Affiche un bandeau rouge en haut de toutes les pages admin si un (ou plusieurs) salarié(s)
// a posé un pointage de Fin sur un ménage il y a plus de 15 min sans soumettre son rapport.
// Le composant se réévalue toutes les 60 secondes via un tick local.
// ══════════════ LECTURE VOCALE (accessibilité pour salariés non lecteurs) ══════════════
// API utilisée : window.speechSynthesis (Web Speech API), 100% native, gratuite, offline une fois la voix
// téléchargée par l'OS. Fonctionne sur Chrome desktop, Safari iOS, Chrome Android, Firefox.
// Activation : toggle dans la topbar (visible pour staff + direction), persisté dans localStorage.
// Déclenchement de lecture : appui long de 500ms sur n'importe quel élément texte.

const LC_VOICE_KEY='lc_voice_mode_v1';

// Sélection de la voix française (la 1ère trouvée). Mémoïsée car getVoices() peut être lent.
let _frVoiceCache=null;
function getFrenchVoice(){
  if(_frVoiceCache)return _frVoiceCache;
  if(!window.speechSynthesis)return null;
  const voices=window.speechSynthesis.getVoices();
  if(!voices.length)return null;
  // Priorité : fr-FR > fr-* > première dispo
  _frVoiceCache=voices.find(v=>v.lang==='fr-FR')||voices.find(v=>v.lang?.startsWith('fr'))||voices[0];
  return _frVoiceCache;
}
// Pré-chargement des voix (certains navigateurs les chargent async)
if(typeof window!=='undefined'&&window.speechSynthesis){
  window.speechSynthesis.onvoiceschanged=()=>{_frVoiceCache=null;getFrenchVoice();};
  getFrenchVoice();
}

// Lit un texte à voix haute. Annule la lecture précédente pour éviter d'empiler les énoncés.
// Nettoie les emojis (qui seraient lus comme "ouvrier du bâtiment de genre non binaire") avant lecture.
function cleanForSpeech(raw){
  if(!raw)return '';
  let t=String(raw);
  // Retire les emojis et pictogrammes Unicode (toutes les zones courantes : Emoji, Symbols, Dingbats,
  // Misc Symbols & Pictographs, Transport, Supplemental Symbols, Flags, Variation Selectors, ZWJ).
  // On utilise la prop \p{Extended_Pictographic} qui couvre tout ça d'un coup.
  try{
    t=t.replace(/\p{Extended_Pictographic}/gu,' ');
    // Retire aussi les sélecteurs de variation (U+FE0F) et joints à largeur nulle (U+200D) qui restent
    t=t.replace(/[\uFE0E\uFE0F\u200D]/g,'');
  }catch(e){
    // Fallback navigateurs sans support \p (très rares en 2026) : regex large sur les ranges principaux
    t=t.replace(/[\u2600-\u27BF\u{1F300}-\u{1FAFF}\u{1F1E6}-\u{1F1FF}]/gu,' ');
  }
  // Normalise les espaces multiples créés par les remplacements
  t=t.replace(/\s+/g,' ').trim();
  return t;
}

function speak(text){
  if(!window.speechSynthesis||!text)return;
  const clean=cleanForSpeech(text);
  if(!clean)return; // si le texte ne contenait QUE des emojis, on ne dit rien
  try{
    window.speechSynthesis.cancel(); // stoppe toute lecture en cours
    const u=new SpeechSynthesisUtterance(clean.slice(0,500)); // garde-fou longueur
    const v=getFrenchVoice();
    if(v)u.voice=v;
    u.lang='fr-FR';
    u.rate=0.95; // un peu plus lent que la valeur par défaut (1.0), plus confortable
    u.pitch=1.0;
    u.volume=1.0;
    window.speechSynthesis.speak(u);
  }catch(e){console.warn('[Voice] Erreur lecture',e);}
}

// Extrait un texte lisible d'un élément DOM. Préfère les attributs sémantiques (aria-label, title)
// quand ils existent (ex: bouton "✕" → aria-label="Fermer"), sinon innerText. Coupe à 500 chars.
function getReadableText(el){
  if(!el)return '';
  // Remonter au plus proche élément interactif ou avec attribut utile
  let cur=el;
  for(let i=0;i<5&&cur;i++){ // max 5 niveaux de remontée
    const aria=cur.getAttribute?.('aria-label');
    if(aria)return aria;
    const title=cur.getAttribute?.('title');
    if(title&&title.length<200)return title; // les tooltips longs sont souvent du texte utile
    cur=cur.parentElement;
  }
  // Sinon prendre le texte brut de l'élément cliqué, en traitant les badges/compteurs
  let txt='';
  // Clone l'élément pour pouvoir le manipuler sans toucher au DOM réel
  try{
    const clone=el.cloneNode(true);
    // Les badges (classes .bc, .nb, .nbc) sont des compteurs : on les remplace par "X nouveautés"
    clone.querySelectorAll('.bc, .nb, .nbc').forEach(b=>{
      const n=parseInt(b.textContent,10);
      if(Number.isFinite(n)&&n>0){
        b.textContent=` (${n} ${n>1?'nouveautés':'nouveauté'})`;
      }else{
        b.textContent='';
      }
    });
    txt=(clone.innerText||clone.textContent||'').trim();
  }catch(e){
    txt=(el.innerText||el.textContent||'').trim();
  }
  return txt.slice(0,500);
}

// Hook React : retourne [voiceMode, setVoiceMode]. Persiste dans localStorage.
function useVoiceMode(){
  const[voice,setVoice]=useState(()=>{
    try{return localStorage.getItem(LC_VOICE_KEY)==='1';}catch(e){return false;}
  });
  const set=v=>{
    setVoice(v);
    try{localStorage.setItem(LC_VOICE_KEY,v?'1':'0');}catch(e){}
  };
  return[voice,set];
}

// Composant : listener global qui détecte l'appui long et lit l'élément touché.
// Quand voiceMode=true, monte un listener pointerdown sur document. Au down → démarre un timer 500ms.
// Si pointerup avant 500ms → annule le timer (= clic normal, on laisse passer).
// Si 500ms atteints → on lit l'élément ET on bloque le click qui suivra (preventDefault sur le prochain click).
function VoiceListener({active}){
  useEffect(()=>{
    if(!active)return;
    let pressTimer=null;
    let longPressTriggered=false;
    let targetEl=null;
    const LONG_PRESS_MS=500;
    const onPointerDown=e=>{
      // Ignore les inputs/textareas/selects pour ne pas casser la saisie texte
      const tag=e.target?.tagName;
      if(['INPUT','TEXTAREA','SELECT'].includes(tag))return;
      longPressTriggered=false;
      targetEl=e.target;
      pressTimer=setTimeout(()=>{
        longPressTriggered=true;
        const txt=getReadableText(targetEl);
        if(txt){speak(txt);}
        // Petit feedback visuel : surligne brièvement l'élément touché
        try{
          const original=targetEl.style.boxShadow;
          targetEl.style.boxShadow='0 0 0 3px var(--mn)';
          targetEl.style.transition='box-shadow .2s';
          setTimeout(()=>{if(targetEl)targetEl.style.boxShadow=original||'';},800);
        }catch(e){}
      },LONG_PRESS_MS);
    };
    const onPointerUp=()=>{
      if(pressTimer){clearTimeout(pressTimer);pressTimer=null;}
    };
    const onPointerMove=e=>{
      // Si l'utilisateur déplace son doigt de plus de quelques pixels, c'est probablement un scroll, on annule
      if(pressTimer&&e.movementY&&Math.abs(e.movementY)>5){clearTimeout(pressTimer);pressTimer=null;}
    };
    // Bloque le click qui suit un appui long (sinon on lit + on déclenche l'action en même temps)
    const onClickCapture=e=>{
      if(longPressTriggered){
        e.preventDefault();
        e.stopPropagation();
        longPressTriggered=false;
      }
    };
    document.addEventListener('pointerdown',onPointerDown,true);
    document.addEventListener('pointerup',onPointerUp,true);
    document.addEventListener('pointercancel',onPointerUp,true);
    document.addEventListener('pointermove',onPointerMove,true);
    document.addEventListener('click',onClickCapture,true);
    return()=>{
      if(pressTimer)clearTimeout(pressTimer);
      document.removeEventListener('pointerdown',onPointerDown,true);
      document.removeEventListener('pointerup',onPointerUp,true);
      document.removeEventListener('pointercancel',onPointerUp,true);
      document.removeEventListener('pointermove',onPointerMove,true);
      document.removeEventListener('click',onClickCapture,true);
      // Annule toute lecture en cours en quittant le mode
      try{window.speechSynthesis?.cancel();}catch(e){}
    };
  },[active]);
  return null;
}

// Composant : bouton toggle de la topbar. Active/désactive le mode lecture vocale.
// À l'activation, déclenche un speak('') muet pour "débloquer" la synthèse vocale sur Safari iOS.
function VoiceToggleButton({voice,setVoice}){
  const[showHelp,setShowHelp]=useState(false);
  if(!window.speechSynthesis)return null; // Navigateur trop vieux, on cache simplement le bouton
  const toggle=()=>{
    if(!voice){
      // Activation : déclenche un test silencieux pour débloquer iOS Safari
      try{
        const u=new SpeechSynthesisUtterance(' ');
        u.volume=0;
        window.speechSynthesis.speak(u);
      }catch(e){}
      // Petit message de bienvenue audio + visuel
      setTimeout(()=>speak('Lecture vocale activée. Appui long pour lire un élément.'),200);
      setVoice(true);
      // Affiche l'aide visuelle pendant 6 secondes la première fois
      try{
        if(!localStorage.getItem('lc_voice_help_seen')){
          setShowHelp(true);
          localStorage.setItem('lc_voice_help_seen','1');
          setTimeout(()=>setShowHelp(false),6000);
        }
      }catch(e){}
    }else{
      try{window.speechSynthesis.cancel();}catch(e){}
      setVoice(false);
    }
  };
  return(<div style={{position:'relative'}}>
    <button onClick={toggle} className={voice?'btn bmn sm':'btn bgh sm'} title={voice?'Désactiver la lecture vocale':'Activer la lecture vocale (appui long pour lire)'}
      style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:12}}>
      <span style={{fontSize:14}}>{voice?'🔊':'🔇'}</span>
      <span>{voice?'Lecture ON':'Lecture'}</span>
    </button>
    {showHelp&&<div style={{position:'absolute',top:'calc(100% + 6px)',right:0,background:'#fff4d6',border:'2px solid var(--or)',borderRadius:10,padding:'10px 14px',fontSize:11,maxWidth:280,zIndex:1000,boxShadow:'0 4px 12px rgba(0,0,0,.15)'}}>
      <div style={{fontWeight:700,marginBottom:4,color:'#7a5a00'}}>💡 Comment ça marche</div>
      <div style={{lineHeight:1.5,color:'#7a5a00'}}>Appuie <strong>longtemps (1 seconde)</strong> sur un texte ou un bouton pour l'entendre lu à voix haute. Tap normal = comportement normal.</div>
    </div>}
  </div>);
}

function PendingReportBanner({als,rep,st,staff,gerants,biens,onGo}){
  const[,setTick]=useState(0);
  useEffect(()=>{const id=setInterval(()=>setTick(t=>t+1),60000);return()=>clearInterval(id);},[]);
  const PENDING_THRESHOLD_MS=15*60*1000; // 15 minutes
  const isMenage=type=>['full','quick','linge','correction'].includes(type);
  const now=Date.now();
  const todayS=today();
  // Tâches ménages DU JOUR avec un pointage end posé il y a >= 15 min mais sans rapport soumis (r.sub null)
  // On limite aux tâches du jour pour éviter que les anciens fantômes (incidents passés) polluent le bandeau.
  const pending=(als||[])
    .filter(l=>l.type==='end'&&l.tid)
    .map(l=>{
      const t=(st||[]).find(x=>x.id===l.tid);
      if(!t||!isMenage(t.type))return null;
      if(t.noReport)return null; // ← rapport rendu optionnel par admin pour cette tâche : pas d'alerte
      if(t.date!==todayS)return null; // ← uniquement les ménages du jour
      const r=(rep||[]).find(x=>x.tid===l.tid);
      if(r&&r.sub)return null; // rapport soumis → OK
      const ageMs=now-new Date(l.ts).getTime();
      if(ageMs<PENDING_THRESHOLD_MS)return null;
      return{l,t,ageMs};
    })
    .filter(Boolean)
    // Dédupe par tid au cas où plusieurs pointages end (on garde le plus récent)
    .reduce((acc,x)=>{const ex=acc.find(a=>a.t.id===x.t.id);if(!ex)acc.push(x);else if(new Date(x.l.ts)>new Date(ex.l.ts)){acc.splice(acc.indexOf(ex),1,x);}return acc;},[])
    .sort((a,b)=>b.ageMs-a.ageMs);
  if(pending.length===0)return null;
  const allWorkers=[...(staff||[]),...((gerants||[]).map(g=>({...g,_gerant:true})))];
  const fmtAge=ms=>{const m=Math.floor(ms/60000);if(m<60)return`${m} min`;const h=Math.floor(m/60);return`${h}h${String(m%60).padStart(2,'0')}`;};
  return(<div style={{background:'#fde0e0',border:'2px solid var(--dg)',borderRadius:12,padding:'12px 16px',marginBottom:12,boxShadow:'0 2px 6px rgba(220,53,69,.15)'}}>
    <div style={{display:'flex',gap:10,alignItems:'flex-start',flexWrap:'wrap'}}>
      <div style={{fontSize:24,lineHeight:1}}>🚨</div>
      <div style={{flex:1,minWidth:220}}>
        <div className="D" style={{fontSize:14,color:'var(--dg)',marginBottom:4}}>
          {pending.length===1?'Rapport de ménage non soumis depuis plus de 15 min':`${pending.length} rapports de ménage non soumis depuis plus de 15 min`}
        </div>
        <div style={{fontSize:11,color:'#7f1d1d',lineHeight:1.5}}>
          {pending.map(({l,t,ageMs})=>{const s=allWorkers.find(w=>w.id===l.sid);const b=(biens||[]).find(x=>x.id===t.bid);return(
            <div key={l.id} style={{padding:'4px 0',borderTop:pending.indexOf(pending.find(p=>p.l.id===l.id))>0?'1px dashed #f5b5b5':'none'}}>
              <strong>{s?fname(s):'?'}{s?._gerant?' (direction)':''}</strong> · {b?.name||'?'} · Fin pointée il y a <strong>{fmtAge(ageMs)}</strong>
              {onGo&&<button onClick={()=>onGo('rap')} className="btn bgh sm" style={{marginLeft:8,fontSize:10,padding:'2px 8px'}}>→ Rapports</button>}
            </div>
          );})}
        </div>
      </div>
    </div>
  </div>);
}

// Bandeau rouge persistant (admin + direction) : demandes d'aide terrain non traitées.
// Source = clé assist7 (Lot A encadrement renforcé, v112). Chaque demande reste
// affichée jusqu'à ce qu'un responsable clique « ✓ Traité » — marquage persistant
// (traite:true), jamais de suppression physique : l'historique reste dans
// SecRH › 🆘 Demandes d'aide. Écriture read-before-write (multi-session sûr).
function AssistanceBanner({assist,setAssist,staff,gerants,sess,onGo}){
  const actives=(assist||[]).filter(a=>!a.traite).sort((a,b)=>String(b.ts).localeCompare(String(a.ts)));
  if(actives.length===0)return null;
  const allWorkers=[...(staff||[]),...((gerants||[]).map(g=>({...g,_gerant:true})))];
  const parNom=sess?.email||sess?.name||'Direction';
  const fmtAge=ts=>{const ms=Date.now()-new Date(ts).getTime();const m=Math.max(0,Math.floor(ms/60000));if(m<60)return`${m} min`;const h=Math.floor(m/60);return`${h}h${String(m%60).padStart(2,'0')}`;};
  const traiter=async(id)=>{
    try{
      const base=await supaGet('assist7')||[];
      const merged=base.map(a=>a.id===id?{...a,traite:true,traitePar:parNom,traiteTs:now()}:a);
      await supaSet('assist7',merged);setAssist(merged);
    }catch(e){console.error('[assist traité] écriture échouée :',e.message);setAssist(as=>(as||[]).map(a=>a.id===id?{...a,traite:true,traitePar:parNom,traiteTs:now()}:a));}
  };
  return(<div style={{background:'#fde0e0',border:'2px solid var(--dg)',borderRadius:12,padding:'12px 16px',marginBottom:12,boxShadow:'0 2px 6px rgba(220,53,69,.15)'}}>
    <div style={{display:'flex',gap:10,alignItems:'flex-start',flexWrap:'wrap'}}>
      <div style={{fontSize:24,lineHeight:1}}>🆘</div>
      <div style={{flex:1,minWidth:220}}>
        <div className="D" style={{fontSize:14,color:'var(--dg)',marginBottom:4}}>
          {actives.length===1?"Demande d'aide d'un salarié sur le terrain":`${actives.length} demandes d'aide de salariés sur le terrain`}
        </div>
        <div style={{fontSize:11,color:'#7f1d1d',lineHeight:1.5}}>
          {actives.map((a,i)=>{const s=allWorkers.find(w=>w.id===a.sid);return(
            <div key={a.id} style={{padding:'5px 0',borderTop:i>0?'1px dashed #f5b5b5':'none',display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',justifyContent:'space-between'}}>
              <div><strong>{s?fname(s):(a.fromName||'?')}</strong>{s?._gerant?' (direction)':''} · {a.motif||"Besoin d'aide"} · il y a <strong>{fmtAge(a.ts)}</strong></div>
              <div style={{display:'flex',gap:6}}>
                {onGo&&<button onClick={()=>onGo('rh')} className="btn bgh sm" style={{fontSize:10,padding:'2px 8px'}}>→ RH</button>}
                <button onClick={()=>traiter(a.id)} className="btn bgh sm" style={{fontSize:10,padding:'2px 10px'}} title="Marquer cette demande comme traitée">✓ Traité</button>
              </div>
            </div>
          );})}
        </div>
      </div>
    </div>
  </div>);
}

// Bandeau rouge des signalements de seuil qualité (dureeAnom7, v154 révisé 11/08/2026) —
// même patron que AssistanceBanner ci-dessus. Contrairement à assist7, l'envoi du
// signalement a déjà débloqué et clôturé le ménage côté salarié/gérant au moment où ce
// bandeau apparaît : il sert à Claire/Franck pour réagir vite (qui, où, pourquoi), pas à
// débloquer quoi que ce soit — « ✓ Traité » se contente de classer le signalement.
function DureeAnomBanner({dureeAnom,setDureeAnom,staff,gerants,sess,onGo,als,rep,controles}){
  const actives=(dureeAnom||[]).filter(a=>!a.traite&&!a.vu).sort((a,b)=>String(b.ts).localeCompare(String(a.ts)));
  if(actives.length===0)return null;
  const allWorkers=[...(staff||[]),...((gerants||[]).map(g=>({...g,_gerant:true})))];
  const fmtAge=ts=>{const ms=Date.now()-new Date(ts).getTime();const m=Math.max(0,Math.floor(ms/60000));if(m<60)return m+' min';const h=Math.floor(m/60);return h+'h'+String(m%60).padStart(2,'0');};
  const fmtMs=ms=>{if(!ms||ms<=0)return null;const m=Math.round(ms/60000);if(m<60)return m+' min';const h=Math.floor(m/60);return h+'h'+String(m%60).padStart(2,'0');};
  const scoreColorFn=sc=>{const n=parseFloat(sc);return{c:n>=3.5?'var(--ok)':n>=2.5?'#7a5a00':n>=1.5?'var(--or)':'var(--dg)',bg:n>=3.5?'#e1f7ee':n>=2.5?'#fff9e0':n>=1.5?'#ffe9d6':'#fde0e0'};};
  const dismiss=async(id)=>{
    try{
      const base=await supaGet('dureeAnom7')||[];
      const merged=base.map(a=>a.id===id?{...a,vu:true}:a);
      await supaSet('dureeAnom7',merged);setDureeAnom(merged);
    }catch(e){console.error('[dureeAnom vu]',e.message);setDureeAnom(as=>(as||[]).map(a=>a.id===id?{...a,vu:true}:a));}
  };
  return(<div style={{background:'#fde0e0',border:'2px solid var(--dg)',borderRadius:12,padding:'12px 16px',marginBottom:12,boxShadow:'0 2px 6px rgba(220,53,69,.15)'}}>
    <div style={{display:'flex',gap:10,alignItems:'flex-start',flexWrap:'wrap'}}>
      <div style={{fontSize:24,lineHeight:1}}>{"\u23f1\ufe0f"}</div>
      <div style={{flex:1,minWidth:220}}>
        <div className="D" style={{fontSize:14,color:'var(--dg)',marginBottom:4}}>
          {actives.length===1?'Signalement de seuil qualit\u00e9':actives.length+' signalements de seuil qualit\u00e9'}
        </div>
        <div style={{fontSize:11,color:'#7f1d1d',lineHeight:1.5}}>
          {actives.map((a,i)=>{
            const s=allWorkers.find(w=>w.id===a.sid);
            const senslbl=a.sens==='sous'?'trop rapide':'trop long';
            const ptg=pointageTache(a.tid,als||[]);
            const dureeLbl=fmtMs(ptg.ms);
            const rapport=(rep||[]).find(r=>r.tid===a.tid);
            const ctrl=rapport?(controles||[]).find(c=>c.repId===rapport.id):null;
            const ctrlPieces=ctrl?.pieces||{};
            const ctrlNotes=Object.values(ctrlPieces).map(p=>p.note).filter(Boolean);
            const ctrlScore=ctrlNotes.length>0?(ctrlNotes.reduce((s,n)=>s+n,0)/ctrlNotes.length).toFixed(1):null;
            const sCol=ctrlScore?scoreColorFn(ctrlScore):null;
            return(
            <div key={a.id} style={{padding:'5px 0',borderTop:i>0?'1px dashed #f5b5b5':'none',display:'flex',gap:8,alignItems:'flex-start',flexWrap:'wrap',justifyContent:'space-between'}}>
              <div style={{flex:1}}>
                <div><strong>{s?fname(s):(a.fromName||'?')}</strong>{s?._gerant?' (direction)':''}{' \u00b7 '}{a.bienName||'?'}{' \u00b7 '}{senslbl}{' \u00b7 il y a '}<strong>{fmtAge(a.ts)}</strong></div>
                <div style={{marginTop:2,color:'var(--ink)',fontStyle:'italic'}}>{a.motif}</div>
                <div style={{marginTop:3,display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>
                  {dureeLbl&&<span>{'Dur\u00e9e m\u00e9nage : '}<strong>{dureeLbl}</strong></span>}
                  {ctrl?<span style={{display:'inline-flex',gap:5,alignItems:'center'}}>
                    <span style={{color:ctrl.verdict==='pret'?'var(--ok)':ctrl.verdict==='nonpret'?'var(--dg)':'var(--si)',fontWeight:600}}>
                      {ctrl.verdict==='pret'?'\u2705 Contr\u00f4le : Pr\u00eat':ctrl.verdict==='nonpret'?'\u26a0\ufe0f Contr\u00f4le : Non pr\u00eat':'\ud83d\udd0d Contr\u00f4le en cours'}
                    </span>
                    {ctrlScore&&<span style={{background:sCol.bg,color:sCol.c,padding:'1px 7px',borderRadius:999,fontWeight:700,fontSize:10}}>{ctrlScore}/4</span>}
                  </span>:<span style={{color:'var(--si)'}}>{'Pas de contr\u00f4le'}</span>}
                </div>
              </div>
              <div style={{display:'flex',gap:6,alignItems:'center',flexShrink:0}}>
                {onGo&&<button onClick={()=>onGo('rh')} className="btn bgh sm" style={{fontSize:10,padding:'2px 8px'}}>{"\u2192 RH"}</button>}
                <button onClick={()=>dismiss(a.id)} style={{background:'none',border:'none',cursor:'pointer',fontSize:18,color:'#7f1d1d',padding:'2px 6px',lineHeight:1}} title="Fermer (reste visible dans Alertes dur\u00e9e)">{"\u2715"}</button>
              </div>
            </div>
          );})}
        </div>
      </div>
    </div>
  </div>);
}


// Clé localStorage du brouillon d'anomalie en cours (écriture-avant-réseau, même principe
// que photo_diag) : permet de survivre à une fermeture/kill de l'app pendant un échec de sync,
// pour pouvoir consigner l'anomalie dans syncano7 même si l'app n'a jamais pu le faire elle-même.
const SYNC_ANO_DRAFT_KEY='sync_ano_draft';

// Bandeau d'erreur de synchronisation Supabase (retry automatique, ~30 clés via useSt) +
// journal persistant des anomalies non résolues (décision Claire du 29/07/2026) :
// - Rouge, tant que ça échoue : pas de fermeture possible, retry jamais interrompu (inchangé).
// - Vert, dès que le retry aboutit : le salarié doit cliquer « Fermer » — la résolution n'est
//   plus silencieuse, ça répond à la volatilité qui faisait qu'un incident passait inaperçu.
// - Seuls les cas où l'app est quittée/rechargée AVANT résolution sont journalisés dans
//   syncano7 (règle explicite : pas de bruit pour les échecs qui se sont résolus tout seuls).
//   Le brouillon local survit à un crash/kill ; il est flushé vers Supabase au démarrage
//   suivant, best-effort — s'il échoue encore (toujours hors-ligne), il reste en place et sera
//   retenté au prochain démarrage.
// ⚠️ Ce journal prouve l'incident, il ne récupère pas la donnée : controles7 (et les autres
// clés useSt hors rep7/als7) n'ont pas de file locale IndexedDB — une donnée en attente est
// réellement perdue si l'app est tuée pendant un échec. Voir chantier B (HANDOFF, en attente).
function SyncErrorBanner({sess,name}){
  const[errors,setErrors]=useState({});
  const[resolved,setResolved]=useState(false);
  const episodeStart=useRef(null); // ts du 1er échec de l'épisode en cours (null = pas d'épisode possédé par cette session)

  useEffect(()=>{
    const onErr=e=>setErrors(prev=>({...prev,[e.detail.key]:{message:e.detail.message,attempt:e.detail.attempt,op:e.detail.op,ts:Date.now()}}));
    const onOk=e=>setErrors(prev=>{if(!prev[e.detail.key])return prev;const n={...prev};delete n[e.detail.key];return n;});
    syncBus.addEventListener('error',onErr);
    syncBus.addEventListener('ok',onOk);
    return()=>{syncBus.removeEventListener('error',onErr);syncBus.removeEventListener('ok',onOk);};
  },[]);
  // Bloquer la fermeture/rechargement tant que des données ne sont pas encore sauvegardées (inchangé)
  useEffect(()=>{
    const hasWriteErrors=Object.values(errors).some(e=>e.op==='write');
    if(!hasWriteErrors)return;
    const handler=e=>{
      e.preventDefault();
      e.returnValue="Donnees non sauvegardees - ne quittez pas cette page.";
      return e.returnValue;
    };
    window.addEventListener('beforeunload',handler);
    return()=>window.removeEventListener('beforeunload',handler);
  },[errors]);
  const keys=Object.keys(errors);

  // Gestion du brouillon local de CETTE session. Ne touche au localStorage que pour un épisode
  // que cette session a elle-même démarré (episodeStart.current non nul) — sinon on ne doit
  // jamais effacer/écraser un brouillon hérité d'une session précédente avant que l'effet de
  // flush (ci-dessous) ait pu le lire.
  useEffect(()=>{
    if(keys.length>0){
      setResolved(false);
      if(episodeStart.current===null)episodeStart.current=Date.now();
      try{
        localStorage.setItem(SYNC_ANO_DRAFT_KEY,JSON.stringify({
          errors,sessType:sess?.type||null,sessId:sess?.id||null,name:name||null,
          firstTs:episodeStart.current,lastTs:Date.now(),
        }));
      }catch(_){}
      return;
    }
    // keys.length===0 : résolu. Seulement si CETTE session possédait l'épisode.
    if(episodeStart.current!==null){
      try{localStorage.removeItem(SYNC_ANO_DRAFT_KEY);}catch(_){}
      setResolved(true);
      episodeStart.current=null;
    }
  },[errors,sess,name]);

  // Au montage (démarrage de l'appli) : un brouillon survivant d'une session précédente est la
  // preuve qu'elle a été quittée pendant un échec non résolu → on le consigne dans syncano7.
  useEffect(()=>{
    (async()=>{
      let raw=null;
      try{raw=localStorage.getItem(SYNC_ANO_DRAFT_KEY);}catch(_){}
      if(!raw)return;
      let draft=null;
      try{draft=JSON.parse(raw);}catch(_){return;}
      if(!draft)return;
      try{
        const base=await supaGet('syncano7')||[];
        const entry={id:'sa'+Date.now()+Math.random().toString(36).slice(2,6),...draft};
        await supaSet('syncano7',[...base,entry]);
        // Ne supprimer que si le brouillon n'a pas changé entre-temps (pas de nouvel épisode
        // démarré par CETTE session pendant le flush — sinon on effacerait un brouillon en cours).
        try{
          if(localStorage.getItem(SYNC_ANO_DRAFT_KEY)===raw)localStorage.removeItem(SYNC_ANO_DRAFT_KEY);
        }catch(_){}
      }catch(e){
        console.error('[SyncErrorBanner] écriture journal anomalies échouée (retentée au prochain démarrage) :',e.message);
      }
    })();
  },[]);

  if(keys.length){
    return(<div style={{position:'fixed',top:0,left:0,right:0,background:'#fee2e2',borderBottom:'2px solid #ef4444',padding:'8px 14px',zIndex:9999,fontSize:11,color:'#7f1d1d',boxShadow:'0 2px 8px rgba(0,0,0,.1)'}}>
      <div style={{fontWeight:700,marginBottom:4}}>⚠️ Erreur de synchronisation Supabase (retry automatique en cours)</div>
      {keys.map(k=>(<div key={k} style={{fontFamily:'monospace',fontSize:10,lineHeight:1.4}}>
        <strong>{k}</strong> · {errors[k].op==='write'?'écriture':'lecture'} · retry en cours ({errors[k].attempt} essais) · {errors[k].message}
      </div>))}
      <div style={{fontStyle:'italic',fontSize:10,marginTop:4,opacity:.85}}>⚠️ Ne fermez pas cette page — les données seront perdues si vous quittez avant la fin du retry.</div>
    </div>);
  }
  if(resolved){
    return(<div style={{position:'fixed',top:0,left:0,right:0,background:'#dcfce7',borderBottom:'2px solid #16a34a',padding:'8px 14px',zIndex:9999,fontSize:11,color:'#14532d',boxShadow:'0 2px 8px rgba(0,0,0,.1)',display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,flexWrap:'wrap'}}>
      <div style={{fontWeight:700}}>✅ Synchronisation rétablie — vos données sont bien enregistrées.</div>
      <button onClick={()=>setResolved(false)} className="btn bgh sm" style={{fontSize:10,padding:'2px 10px'}}>Fermer</button>
    </div>);
  }
  return null;
}

// ══════════════ CHANGEMENT DE MOT DE PASSE ══════════════
function ChangePasswordModal({onClose}){
  const[oldPwd,setOldPwd]=React.useState('');
  const[newPwd,setNewPwd]=React.useState('');
  const[confirmPwd,setConfirmPwd]=React.useState('');
  const[err,setErr]=React.useState('');
  const[ok,setOk]=React.useState(false);
  const[loading,setLoading]=React.useState(false);
  const[showOld,setShowOld]=React.useState(false);
  const[showNew,setShowNew]=React.useState(false);

  const handleChange=async()=>{
    setErr('');
    if(!oldPwd.trim()||!newPwd.trim()||!confirmPwd.trim()){setErr('Tous les champs sont obligatoires.');return;}
    if(newPwd.length<8){setErr('Le nouveau mot de passe doit faire au moins 8 caractères.');return;}
    if(newPwd!==confirmPwd){setErr('Les deux nouveaux mots de passe ne correspondent pas.');return;}
    if(newPwd===oldPwd){setErr("Le nouveau mot de passe doit être différent de l'ancien.");return;}
    setLoading(true);
    try{
      let email='';
      try{
        const payload=SUPA_TOKEN.split('.')[1];
        const decoded=JSON.parse(atob(payload.replace(/-/g,'+').replace(/_/g,'/')));
        email=decoded.email||'';
      }catch{setErr('Impossible de vérifier votre identité. Reconnectez-vous.');setLoading(false);return;}
      if(!email){setErr('Email introuvable dans la session. Reconnectez-vous.');setLoading(false);return;}
      const checkRes=await fetch(`${SUPA_URL}/auth/v1/token?grant_type=password`,{
        method:'POST',
        headers:{'Content-Type':'application/json','apikey':SUPA_KEY},
        body:JSON.stringify({email,password:oldPwd})
      });
      if(!checkRes.ok){setErr('Ancien mot de passe incorrect.');setLoading(false);return;}
      const updateRes=await fetch(`${SUPA_URL}/auth/v1/user`,{
        method:'PUT',
        headers:{'Content-Type':'application/json','apikey':SUPA_KEY,'Authorization':`Bearer ${SUPA_TOKEN}`},
        body:JSON.stringify({password:newPwd})
      });
      if(!updateRes.ok){
        const d=await updateRes.json();
        setErr(d?.msg||d?.message||'Erreur lors du changement de mot de passe.');
        setLoading(false);return;
      }
      setOk(true);
    }catch(e){setErr('Erreur réseau. Réessayez.');setLoading(false);}
  };

  return(
    <div className="mbk" onClick={onClose}>
      <div style={{background:'var(--bg)',borderRadius:16,padding:26,maxWidth:380,width:'100%',margin:'0 14px'}} onClick={e=>e.stopPropagation()}>
        <div className="D" style={{fontSize:18,marginBottom:16}}>🔑 Changer le mot de passe</div>
        {ok?(
          <div>
            <div style={{background:'#e0fbe0',border:'1px solid #86efac',borderRadius:9,padding:'12px 14px',fontSize:13,color:'#166534',marginBottom:16}}>
              ✅ Mot de passe modifié avec succès !<br/>
              <span style={{fontSize:11,opacity:.8}}>Pour des raisons de sécurité, vous allez être déconnecté(e).</span>
            </div>
            <button onClick={()=>{try{localStorage.removeItem('supa_session');}catch{}window.location.href=window.location.pathname;}} className="btn bor" style={{width:'100%',justifyContent:'center'}}>Se déconnecter →</button>
          </div>
        ):(
          <div style={{display:'flex',flexDirection:'column',gap:13}}>
            <div>
              <label className="fl">Mot de passe actuel</label>
              <div style={{position:'relative'}}>
                <input type={showOld?'text':'password'} value={oldPwd} onChange={e=>{setOldPwd(e.target.value);setErr('');}} placeholder="••••••••" style={{paddingRight:40}} autoFocus/>
                <button onClick={()=>setShowOld(v=>!v)} style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',fontSize:14,color:'var(--si)',padding:4}} tabIndex={-1}>{showOld?'🙈':'👁'}</button>
              </div>
            </div>
            <div>
              <label className="fl">Nouveau mot de passe <span style={{fontSize:10,color:'var(--si)'}}>(8 car. min.)</span></label>
              <div style={{position:'relative'}}>
                <input type={showNew?'text':'password'} value={newPwd} onChange={e=>{setNewPwd(e.target.value);setErr('');}} placeholder="••••••••" style={{paddingRight:40}}/>
                <button onClick={()=>setShowNew(v=>!v)} style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',fontSize:14,color:'var(--si)',padding:4}} tabIndex={-1}>{showNew?'🙈':'👁'}</button>
              </div>
            </div>
            <div>
              <label className="fl">Confirmer le nouveau mot de passe</label>
              <input type="password" value={confirmPwd} onChange={e=>{setConfirmPwd(e.target.value);setErr('');}} placeholder="••••••••" onKeyDown={e=>{if(e.key==='Enter')handleChange();}}/>
            </div>
            {newPwd.length>0&&<div style={{height:4,borderRadius:2,background:'var(--li)',overflow:'hidden'}}>
              <div style={{height:'100%',borderRadius:2,transition:'width .3s,background .3s',width:`${Math.min(100,newPwd.length*8)}%`,background:newPwd.length<8?'#ef4444':newPwd.length<12?'#f59e0b':'#22c55e'}}/>
            </div>}
            {err&&<div style={{background:'#fde0e0',border:'1px solid #f5b0b0',borderRadius:9,padding:'9px 13px',fontSize:12,color:'#8a1010'}}>⚠️ {err}</div>}
            <div style={{display:'flex',gap:8,marginTop:4}}>
              <button onClick={onClose} className="btn bgh" style={{flex:1,justifyContent:'center'}}>Annuler</button>
              <button onClick={handleChange} disabled={loading} className="btn bor" style={{flex:2,justifyContent:'center'}}>{loading?'Vérification…':'Changer le mot de passe'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════ NOTIFICATION BUTTON (commit 2.3) ══════════════
// Petit bouton dans la topbar permettant à l'utilisateur (salarié, direction,
// prestataire, admin) d'autoriser les notifications push sur cet appareil.
// L'autorisation est une porte technique : sans elle, le navigateur bloque tout.
// Le contenu des notifications (qui reçoit quoi) est défini côté admin (étape 3).
function NotificationButton({sess}){
  // États possibles : 'unsupported' | 'denied' | 'unsubscribed' | 'subscribed' | 'loading'
  const[state,setState]=useState('loading');
  const[busy,setBusy]=useState(false);

  // Détecter l'état actuel au mount + à chaque changement de session
  useEffect(()=>{
    let cancelled=false;
    (async()=>{
      try{
        if(!('serviceWorker' in navigator)||!('PushManager' in window)||!('Notification' in window)){
          if(!cancelled)setState('unsupported');return;
        }
        if(Notification.permission==='denied'){
          if(!cancelled)setState('denied');return;
        }
        const reg=await navigator.serviceWorker.ready;
        const sub=await reg.pushManager.getSubscription();
        if(cancelled)return;
        if(sub&&Notification.permission==='granted'){
          setState('subscribed');
        }else{
          setState('unsubscribed');
        }
      }catch(e){
        console.warn('[NotificationButton] init error',e);
        if(!cancelled)setState('unsubscribed');
      }
    })();
    return()=>{cancelled=true;};
  },[sess?.id,sess?.type]);

  // Enregistre l'abonnement push dans Supabase (table push_subscriptions)
  const saveSubscriptionToSupabase=async(subscription)=>{
    const json=subscription.toJSON();
    const payload={
      endpoint:json.endpoint,
      p256dh:json.keys?.p256dh||'',
      auth_secret:json.keys?.auth||'',
      user_agent:navigator.userAgent.slice(0,255),
      last_used_at:new Date().toISOString(),
      // ─── NOUVEAU (étape 5) : permet à l'Edge Function d'identifier QUI est abonné ───
      // Sans ces champs, on aurait juste auth.uid() côté Supabase, ce qui ne permet pas
      // de faire le lien avec nos tables JSON (staff, gerants, prov, clients).
      // Cas particulier admin : EMAIL_ROLES déclare {type:'admin'} sans id, donc
      // sess.id est undefined. On fallback sur 'admin' pour rester cohérent avec
      // le bus (qui résout admin → {type:'admin', id:'admin'}).
      user_role:sess?.type||null,
      user_internal_id:sess?.id||(sess?.type==='admin'?'admin':null)
    };
    // Upsert via REST API Supabase (le user_id sera renseigné automatiquement par RLS auth.uid())
    // On envoie via fetch avec le token utilisateur (RLS exige authenticated)
    const r=await fetch(`${SUPA_URL}/rest/v1/push_subscriptions`,{
      method:'POST',
      headers:{
        'apikey':SUPA_KEY,
        'Authorization':`Bearer ${supaAuthHeader()}`,
        'Content-Type':'application/json',
        'Prefer':'resolution=merge-duplicates,return=minimal'
      },
      body:JSON.stringify({...payload,user_id:undefined}) // user_id rempli côté serveur via RLS
    });
    // En cas de 401, on tente un refresh puis on retente une fois
    if(r.status===401&&SUPA_REFRESH&&await supaRefreshToken()){
      const r2=await fetch(`${SUPA_URL}/rest/v1/push_subscriptions`,{
        method:'POST',
        headers:{
          'apikey':SUPA_KEY,
          'Authorization':`Bearer ${supaAuthHeader()}`,
          'Content-Type':'application/json',
          'Prefer':'resolution=merge-duplicates,return=minimal'
        },
        body:JSON.stringify({...payload,user_id:undefined})
      });
      if(!r2.ok)throw new Error('Sauvegarde abonnement échouée (HTTP '+r2.status+')');
    }else if(!r.ok){
      throw new Error('Sauvegarde abonnement échouée (HTTP '+r.status+')');
    }
  };

  // Au clic : activer les notifs
  const enable=async()=>{
    if(busy)return;
    setBusy(true);
    try{
      // 1. Demander la permission au navigateur
      const perm=await Notification.requestPermission();
      if(perm!=='granted'){
        if(perm==='denied'){setState('denied');alert('Les notifications ont été bloquées. Pour les réactiver, allez dans les paramètres de votre navigateur et autorisez les notifications pour ce site.');}
        else setState('unsubscribed');
        return;
      }
      // 2. S'abonner au push manager avec la clé VAPID
      const reg=await navigator.serviceWorker.ready;
      let sub=await reg.pushManager.getSubscription();
      if(!sub){
        sub=await reg.pushManager.subscribe({
          userVisibleOnly:true,
          applicationServerKey:urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
        });
      }
      // 3. Sauvegarder l'abonnement dans Supabase
      await saveSubscriptionToSupabase(sub);
      setState('subscribed');
      console.log('[NotificationButton] abonnement créé et sauvegardé');
    }catch(e){
      console.error('[NotificationButton] activation échouée',e);
      alert("Impossible d'activer les notifications : "+e.message);
    }finally{
      setBusy(false);
    }
  };

  // Au clic : désactiver les notifs sur cet appareil
  const disable=async()=>{
    if(busy)return;
    if(!confirm('Désactiver les notifications sur cet appareil ?'))return;
    setBusy(true);
    try{
      const reg=await navigator.serviceWorker.ready;
      const sub=await reg.pushManager.getSubscription();
      if(sub){
        // Supprimer l'abonnement de Supabase
        const endpoint=sub.endpoint;
        await fetch(`${SUPA_URL}/rest/v1/push_subscriptions?endpoint=eq.${encodeURIComponent(endpoint)}`,{
          method:'DELETE',
          headers:{'apikey':SUPA_KEY,'Authorization':`Bearer ${supaAuthHeader()}`}
        });
        await sub.unsubscribe();
      }
      setState('unsubscribed');
      console.log('[NotificationButton] abonnement désactivé');
    }catch(e){
      console.error('[NotificationButton] désactivation échouée',e);
      alert('Erreur lors de la désactivation : '+e.message);
    }finally{
      setBusy(false);
    }
  };

  // Rendu selon l'état
  if(state==='loading')return null;
  if(state==='unsupported')return null; // navigateur trop vieux ou iOS sans PWA installée

  if(state==='subscribed'){
    return(<button onClick={disable} disabled={busy} className="btn bgh sm" title="Notifications activées sur cet appareil — cliquer pour désactiver" style={{color:'var(--ok)'}}>
      🔔 <span style={{marginLeft:4}}>Notifs ON</span>
    </button>);
  }
  if(state==='denied'){
    return(<button onClick={()=>alert('Les notifications sont bloquées par votre navigateur. Pour les autoriser : cliquez sur l\'icône 🔒 ou ⓘ à gauche de l\'adresse du site, puis autorisez les notifications.')} className="btn bgh sm" title="Notifications bloquées par le navigateur" style={{color:'var(--si)'}}>
      🔕 <span style={{marginLeft:4}}>Notifs bloquées</span>
    </button>);
  }
  // unsubscribed
  return(<button onClick={enable} disabled={busy} className="btn bgh sm" title="Activer les notifications sur cet appareil">
    🔔 <span style={{marginLeft:4}}>{busy?'…':'Activer notifs'}</span>
  </button>);
}

// ══════════════ NOTIFICATIONS — Panneau admin (étape 3) ══════════════
// Catalogue des événements métier que l'admin peut activer/configurer.
// Stockage : clé `notifConfig` dans app_data (via useSt).
// Branchement effectif des événements = étape 4 (bus notifEventBus).
// Pour l'instant, ce panneau ne fait QUE stocker la config. Aucun effet métier.

// Rôles destinataires possibles, avec libellé et couleur (pour les puces)
const NOTIF_ROLES={
  admin:    {label:'Admin',        color:'var(--ye)', textOnDark:false},
  gerant:   {label:'Direction',    color:'var(--bl)', textOnDark:true},
  assignee: {label:'Concerné(e)',  color:'var(--mn)', textOnDark:true},
  client:   {label:'Propriétaire', color:'var(--pk)', textOnDark:false},
};

// Canaux possibles
const NOTIF_CHANNELS={
  push:    {label:'Push',   icon:'🔔', desc:'Notification système sur l\'appareil'},
  bandeau_inapp:{label:'In-app',icon:'📢', desc:'Bandeau affiché dans l\'app quand elle est ouverte'},
  sms:     {label:'SMS',    icon:'📱', desc:'SMS (OVH) — disponible plus tard'},
};

// Catalogue d'événements. `delayBased` indique si l'event a un délai en minutes.
// `allowedRoles` = quels rôles destinataires sont valides pour cet event.
// `allowedChannels` = quels canaux sont possibles (sms est grisé sauf message_recu).
// → NOTIF_EVENTS + defaultNotifConfig déplacés dans src/notifBus.js
// Petite puce colorée pour un rôle (réutilisée dans les checkboxes de destinataires)
function NotifRoleChip({roleId,active,disabled,onToggle}){
  const r=NOTIF_ROLES[roleId];if(!r)return null;
  return(<label style={{
    display:'inline-flex',alignItems:'center',gap:6,padding:'5px 11px',borderRadius:999,
    background:active?r.color:'transparent',
    color:active?(r.textOnDark?'white':'var(--ink)'):'var(--si)',
    border:active?`1.5px solid ${r.color}`:'1.5px solid var(--ls)',
    cursor:disabled?'not-allowed':'pointer',opacity:disabled?0.4:1,
    fontSize:12,fontWeight:600,transition:'all .12s',userSelect:'none'
  }}>
    <input type="checkbox" checked={active} disabled={disabled} onChange={onToggle} style={{display:'none'}}/>
    <span style={{
      display:'inline-block',width:9,height:9,borderRadius:'50%',
      background:active?(r.textOnDark?'white':'var(--ink)'):r.color,
      opacity:active?1:0.5
    }}/>
    {r.label}
  </label>);
}

// Idem pour un canal
function NotifChannelChip({channelId,active,disabled,onToggle,disabledReason}){
  const c=NOTIF_CHANNELS[channelId];if(!c)return null;
  return(<label title={disabled&&disabledReason?disabledReason:c.desc} style={{
    display:'inline-flex',alignItems:'center',gap:6,padding:'5px 11px',borderRadius:8,
    background:active?'var(--ink)':'transparent',color:active?'white':'var(--si)',
    border:active?'1.5px solid var(--ink)':'1.5px solid var(--ls)',
    cursor:disabled?'not-allowed':'pointer',opacity:disabled?0.4:1,
    fontSize:12,fontWeight:600,transition:'all .12s',userSelect:'none'
  }}>
    <input type="checkbox" checked={active} disabled={disabled} onChange={onToggle} style={{display:'none'}}/>
    <span>{c.icon}</span>{c.label}
  </label>);
}

// Une ligne d'événement dans le tableau
function NotifEventRow({event,cfg,onChange}){
  const isOff=!cfg.enabled;
  const toggleEnabled=()=>onChange({...cfg,enabled:!cfg.enabled});
  const setDelay=v=>{const n=Math.max(0,Math.min(999,parseInt(v,10)||0));onChange({...cfg,delayMin:n});};
  const toggleRole=role=>{
    const has=cfg.recipients.includes(role);
    onChange({...cfg,recipients:has?cfg.recipients.filter(r=>r!==role):[...cfg.recipients,role]});
  };
  const toggleChannel=ch=>{
    const has=cfg.channels.includes(ch);
    onChange({...cfg,channels:has?cfg.channels.filter(c=>c!==ch):[...cfg.channels,ch]});
  };
  // SMS toujours désactivé sauf pour message_recu (où il est activable mais grisé tant que OVH pas branché)
  const smsAllowedForThisEvent=event.allowedChannels.includes('sms');
  const smsBackendReady=false; // bascule à true le jour où l'Edge Function SMS est en place

  return(<div style={{
    display:'flex',gap:14,padding:'14px 16px',borderRadius:12,
    background:isOff?'var(--li)':'white',border:'1px solid var(--li)',
    marginBottom:8,alignItems:'flex-start',flexWrap:'wrap',opacity:isOff?0.7:1,transition:'all .15s'
  }}>
    {/* Toggle */}
    <button onClick={toggleEnabled} title={cfg.enabled?'Désactiver':'Activer'} style={{
      flexShrink:0,width:44,height:26,borderRadius:999,border:'none',
      background:cfg.enabled?'var(--ok)':'var(--ls)',position:'relative',cursor:'pointer',
      transition:'all .15s',padding:0
    }}>
      <span style={{
        position:'absolute',top:2,left:cfg.enabled?20:2,width:22,height:22,
        borderRadius:'50%',background:'white',transition:'left .15s',
        boxShadow:'0 1px 3px rgba(0,0,0,0.2)'
      }}/>
    </button>
    {/* Texte + délai */}
    <div style={{flex:'1 1 200px',minWidth:200}}>
      <div className="D" style={{fontSize:14,marginBottom:2}}>{event.label}</div>
      <div style={{fontSize:11,color:'var(--si)',lineHeight:1.4}}>{event.desc}</div>
      {event.delayBased&&<div style={{marginTop:6,display:'flex',alignItems:'center',gap:6,fontSize:12,color:'var(--si)'}}>
        <span>Délai :</span>
        <input type="number" min={0} max={999} value={cfg.delayMin} onChange={e=>setDelay(e.target.value)} disabled={isOff} style={{
          width:60,padding:'3px 6px',borderRadius:6,border:'1px solid var(--ls)',fontSize:12,fontFamily:'inherit'
        }}/>
        <span>min</span>
      </div>}
    </div>
    {/* Destinataires */}
    <div style={{flex:'1 1 220px',minWidth:200}}>
      <div style={{fontSize:10,color:'var(--si)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:5,fontWeight:600}}>Destinataires</div>
      <div style={{display:'flex',flexWrap:'wrap',gap:5}}>
        {event.allowedRoles.map(r=>(
          <NotifRoleChip key={r} roleId={r} active={cfg.recipients.includes(r)} disabled={isOff} onToggle={()=>toggleRole(r)}/>
        ))}
      </div>
    </div>
    {/* Canaux */}
    <div style={{flex:'1 1 220px',minWidth:200}}>
      <div style={{fontSize:10,color:'var(--si)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:5,fontWeight:600}}>Canaux</div>
      <div style={{display:'flex',flexWrap:'wrap',gap:5}}>
        {event.allowedChannels.filter(c=>c!=='sms').map(c=>(
          <NotifChannelChip key={c} channelId={c} active={cfg.channels.includes(c)} disabled={isOff} onToggle={()=>toggleChannel(c)}/>
        ))}
        {smsAllowedForThisEvent&&<NotifChannelChip channelId="sms" active={false} disabled={true}
          disabledReason="SMS via OVH — disponible une fois le service activé"
          onToggle={()=>{}}/>}
      </div>
    </div>
  </div>);
}

// ── Gestion des jours fériés (section Calendrier) ──
function SecCalendrier({feries,setFeries}){
  const now=new Date();
  const[annee,setAnnee]=useState(now.getFullYear());
  const[nouvelle,setNouvelle]=useState('');
  const MOIS_COURT=['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];
  const JOURS=['Dim','Lun','Mar','Mer','Jeu','Ven','Sam'];

  const feriesAnnee=(feries||[]).filter(d=>d.startsWith(String(annee))).sort();

  const labelJour=(dateStr)=>{
    const d=new Date(dateStr+'T12:00:00');
    return JOURS[d.getDay()]+' '+d.getDate()+' '+MOIS_COURT[d.getMonth()];
  };

  const ajouter=()=>{
    if(!nouvelle) return;
    if((feries||[]).includes(nouvelle)){alert('Cette date est déjà dans la liste.');return;}
    setFeries([...(feries||[]),nouvelle].sort());
    setNouvelle('');
  };

  const supprimer=(dateStr)=>{
    if(!window.confirm('Supprimer le jour férié du '+labelJour(dateStr)+' ?')) return;
    setFeries((feries||[]).filter(d=>d!==dateStr));
  };

  const anneesDispos=Array.from({length:5},(_,i)=>now.getFullYear()-1+i);

  return(<div style={{maxWidth:640,margin:'0 auto',padding:'20px 0'}}>
    <div className="D" style={{fontSize:22,marginBottom:4}}>📅 Calendrier — Jours fériés</div>
    <div style={{fontSize:13,color:'var(--si)',marginBottom:20}}>
      Les jours fériés configurés ici déclenchent automatiquement la majoration dimanche/férié sur les séjours dont le départ tombe ce jour (si le bien a un % configuré).
      Les dimanches sont détectés automatiquement sans configuration.
    </div>
    <div style={{display:'flex',gap:10,alignItems:'center',marginBottom:18}}>
      <label style={{fontSize:13,fontWeight:600}}>Année :</label>
      <select value={annee} onChange={e=>setAnnee(Number(e.target.value))} style={{width:'auto',fontSize:14,fontWeight:700}}>
        {anneesDispos.map(a=><option key={a} value={a}>{a}</option>)}
      </select>
      <span style={{fontSize:12,color:'var(--si)',marginLeft:8}}>{feriesAnnee.length} jour{feriesAnnee.length!==1?'s':''} configuré{feriesAnnee.length!==1?'s':''}</span>
    </div>
    <div style={{background:'var(--cr)',borderRadius:10,padding:'14px 16px',marginBottom:20,display:'flex',gap:10,alignItems:'flex-end',flexWrap:'wrap'}}>
      <div style={{flex:1,minWidth:180}}>
        <label style={{fontSize:12,fontWeight:600,display:'block',marginBottom:4}}>Ajouter un jour férié</label>
        <input type="date" value={nouvelle} onChange={e=>setNouvelle(e.target.value)}
          style={{width:'100%',fontSize:14,padding:'6px 10px',borderRadius:8,border:'1.5px solid var(--li)'}}/>
      </div>
      <button onClick={ajouter} disabled={!nouvelle} className="btn bor" style={{whiteSpace:'nowrap'}}>＋ Ajouter</button>
    </div>
    {feriesAnnee.length===0
      ?<div style={{color:'var(--si)',fontSize:13,padding:'20px 0',textAlign:'center'}}>Aucun jour férié configuré pour {annee}.</div>
      :<div style={{display:'flex',flexDirection:'column',gap:6}}>
        {feriesAnnee.map(d=>{
          const estDim=new Date(d+'T12:00:00').getDay()===0;
          return(<div key={d} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'var(--bg)',borderRadius:9,border:'1px solid var(--li)'}}>
            <span style={{fontSize:18}}>{estDim?'☀️':'🏳️'}</span>
            <span style={{fontWeight:600,fontSize:14,flex:1}}>{labelJour(d)}</span>
            <span style={{fontSize:12,color:'var(--si)',fontFamily:'monospace'}}>{d}</span>
            {estDim&&<span style={{fontSize:11,color:'#b45309',background:'#fef3c7',padding:'2px 7px',borderRadius:99}}>aussi dimanche</span>}
            <button onClick={()=>supprimer(d)} className="btn bdg sm">🗑️</button>
          </div>);
        })}
      </div>
    }
    <div style={{marginTop:24,padding:'12px 14px',background:'var(--bg)',borderRadius:9,fontSize:12,color:'var(--si)',border:'1px solid var(--li)'}}>
      💡 <strong>Fériés spécifiques à l'Alsace :</strong> Vendredi saint et 26 décembre (Saint-Étienne) — à ajouter en plus des 11 fériés nationaux.
    </div>
  </div>);
}

function SecNotifs({notifConfig,setNotifConfig}){
  // Sécurité : si la config est nulle ou un event manque, on fusionne avec les défauts
  const cfg=React.useMemo(()=>{
    const def=defaultNotifConfig();
    const merged={...def};
    if(notifConfig&&typeof notifConfig==='object'){
      Object.keys(def).forEach(k=>{
        if(notifConfig[k]&&typeof notifConfig[k]==='object')merged[k]={...def[k],...notifConfig[k]};
      });
    }
    return merged;
  },[notifConfig]);

  const updateOne=(eventId,newCfg)=>setNotifConfig({...cfg,[eventId]:newCfg});
  const resetAll=()=>{
    if(window.confirm('Restaurer les valeurs par défaut pour TOUS les événements ?\n\nVos réglages personnalisés seront perdus.'))
      setNotifConfig(defaultNotifConfig());
  };

  // Grouper par section
  const sections={};
  NOTIF_EVENTS.forEach(e=>{(sections[e.section]=sections[e.section]||[]).push(e);});

  // Stats : combien d'events activés
  const activeCount=NOTIF_EVENTS.filter(e=>cfg[e.id]?.enabled).length;

  return(<div>
    {/* Bandeau intro */}
    <div style={{
      background:'var(--cr)',border:'1px solid var(--ls)',borderRadius:12,padding:'14px 18px',
      marginBottom:16,display:'flex',gap:12,alignItems:'flex-start',flexWrap:'wrap'
    }}>
      <div style={{fontSize:24}}>🔔</div>
      <div style={{flex:'1 1 280px'}}>
        <div className="D" style={{fontSize:16,marginBottom:4}}>Configuration des notifications</div>
        <div style={{fontSize:12,color:'var(--si)',lineHeight:1.5}}>
          Choisissez quels événements déclenchent une notification, à qui elle est envoyée, et par quel canal.
          Les modifications sont enregistrées automatiquement.
          <br/><strong>Note :</strong> le canal SMS sera disponible une fois OVH activé.
          Le canal In-app prépare le futur affichage de bandeaux dans l'app (à venir).
        </div>
      </div>
      <div style={{
        background:'var(--ink)',color:'white',padding:'6px 12px',borderRadius:999,
        fontSize:12,fontWeight:600,whiteSpace:'nowrap'
      }}>
        {activeCount} / {NOTIF_EVENTS.length} actif{activeCount>1?'s':''}
      </div>
    </div>

    {/* Sections */}
    {Object.keys(sections).map(sectionName=>(
      <div key={sectionName} style={{marginBottom:24}}>
        <div className="D" style={{fontSize:15,marginBottom:10,color:'var(--ink)',display:'flex',alignItems:'center',gap:8}}>
          <span style={{width:4,height:18,background:'var(--or)',borderRadius:2}}/>
          {sectionName}
          <span style={{fontSize:11,color:'var(--si)',fontWeight:400,letterSpacing:0}}>
            · {sections[sectionName].length} événement{sections[sectionName].length>1?'s':''}
          </span>
        </div>
        {sections[sectionName].map(ev=>(
          <NotifEventRow key={ev.id} event={ev} cfg={cfg[ev.id]} onChange={n=>updateOne(ev.id,n)}/>
        ))}
      </div>
    ))}

    {/* Reset */}
    <div style={{
      marginTop:20,paddingTop:16,borderTop:'1px solid var(--li)',
      display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10
    }}>
      <div style={{fontSize:11,color:'var(--si)'}}>
        ⓘ La configuration est partagée entre tous les admins. Les modifs sont enregistrées immédiatement.
      </div>
      <button onClick={resetAll} className="btn bgh sm">
        ↺ Restaurer les valeurs par défaut
      </button>
    </div>
  </div>);
}

// ══════════════ NOTIFICATIONS — Bus d'événements (étape 4) ══════════════
// notifEventBus reçoit les événements émis aux points clés de l'app,
// les filtre selon notifConfig, résout les destinataires, et dispatche :
//   - canal 'push'          → log console (étape 5 = Edge Function réelle)
//   - canal 'bandeau_inapp' → toast in-app (étape future = stack visible pour tous)
//   - canal 'sms'           → log console (étape 6 = OVH)
// Pendant cette phase de mise au point : un toast de debug discret est aussi
// affiché côté admin pour CHAQUE événement tiré, pour visualiser le bus en action.

// → notifToastStore déplacé dans src/notifBus.js

function useNotifToasts(){
  const[list,setList]=React.useState(notifToastStore.list());
  React.useEffect(()=>notifToastStore.subscribe(setList),[]);
  return list;
}

// → notifContextRef, setNotifContext, resolveRecipients, notifEventBus déplacés dans src/notifBus.js

// ─── Composant : Stack de toasts (visible pour admin uniquement) ───
function NotifToastStack(){
  const toasts=useNotifToasts();
  if(toasts.length===0)return null;
  return(<div style={{
    position:'fixed',top:14,right:14,zIndex:9000,
    display:'flex',flexDirection:'column',gap:8,maxWidth:380,pointerEvents:'none'
  }}>
    {toasts.slice(-5).map(t=>(<NotifToastItem key={t.id} toast={t}/>))}
  </div>);
}

function NotifToastItem({toast}){
  const[fading,setFading]=React.useState(false);
  const ev=NOTIF_EVENTS.find(e=>e.id===toast.eventId);
  const sectionColor={
    'Pointages':'var(--bl)',
    'Tâches':'var(--or)',
    'Rapports & qualité':'var(--ye)',
    'Messagerie':'var(--mn)',
  }[ev?.section]||'var(--ink)';
  return(<div style={{
    background:'white',border:`1.5px solid ${sectionColor}`,borderLeft:`5px solid ${sectionColor}`,
    borderRadius:10,padding:'10px 12px',boxShadow:'0 4px 12px rgba(0,0,0,0.12)',
    pointerEvents:'auto',opacity:fading?0:1,transition:'opacity .3s',
    fontSize:12,lineHeight:1.4
  }}>
    <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:3}}>
      <span style={{fontSize:13,fontWeight:700,color:sectionColor}}>🔔 {toast.title}</span>
      {toast.count>1&&<span className="bc" style={{background:sectionColor}}>×{toast.count}</span>}
      <button onClick={()=>{setFading(true);setTimeout(()=>notifToastStore.dismiss(toast.id),300);}} style={{
        marginLeft:'auto',border:'none',background:'transparent',color:'var(--si)',
        cursor:'pointer',fontSize:14,padding:0,lineHeight:1
      }} title="Fermer">✕</button>
    </div>
    <div style={{color:'var(--si)',fontSize:11}}>
      {toast.debug
        ? <span>→ <strong style={{color:'var(--ink)'}}>{toast.recipients}</strong>
            {toast.channels?.length>0&&<span> · {toast.channels.map(c=>NOTIF_CHANNELS[c]?.icon).filter(Boolean).join(' ')}</span>}</span>
        : <span>Nouvelle notification</span>}
    </div>
    {toast.body&&<div style={{
      marginTop:4,fontSize:11,color:'var(--ink)',
      background:'var(--cr)',padding:'4px 7px',borderRadius:6,
      whiteSpace:'pre-wrap',wordBreak:'break-word'
    }}>{toast.body}</div>}
    {toast.debug&&<div style={{fontSize:9,color:'var(--si)',marginTop:4,fontStyle:'italic'}}>
      ⓘ Mode debug — toast visible pour admin uniquement
    </div>}
  </div>);
}

// ═══════════════════════════════════════════════════════════════════
// FIN SCRIPT 8 — CONTENU & INTERFACES (pointages, rapports, chat, UIs racine,
// guides, login, bannières, notifications, calendrier)

// ══════════════ EXPÉDITION — EMAIL + SIMULATEUR PUBLIC ══════════════

// Génère un token unique pour le simulateur public
function genExpToken() {
  return Math.random().toString(36).slice(2, 12);
}

// Envoi email objet trouvé via Edge Function send-ot-email (sans PDF)
async function envoyerEmailOT({ toEmail, toName, objet, bien, simulUrl }) {
  const htmlContent =
    '<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#2a2520;line-height:1.7;">'
    + `<p>Bonjour${toName ? (' ' + toName) : ''},</p>`
    + '<p>Nous avons trouvé un objet vous appartenant dans notre logement et souhaitons vous le restituer dans les meilleures conditions.</p>'
    + '<table style="border-collapse:collapse;width:100%;margin:12px 0;">'
    + '<tr><td style="padding:6px 10px;background:#f5f0eb;font-weight:600;width:40%;">Objet</td>'
    + `<td style="padding:6px 10px;background:#faf7f4;">${objet.desc || '—'}</td></tr>`
    + '<tr><td style="padding:6px 10px;background:#f5f0eb;font-weight:600;">Référence</td>'
    + `<td style="padding:6px 10px;background:#faf7f4;">${objet.ref || '—'}</td></tr>`
    + '<tr><td style="padding:6px 10px;background:#f5f0eb;font-weight:600;">Logement</td>'
    + `<td style="padding:6px 10px;background:#faf7f4;">${bien || '—'}</td></tr>`
    + '<tr><td style="padding:6px 10px;background:#f5f0eb;font-weight:600;">Découvert le</td>'
    + `<td style="padding:6px 10px;background:#faf7f4;">${objet.date || '—'}</td></tr>`
    + '</table>'
    + '<p>Vous avez deux options :</p>'
    + '<p style="margin:14px 0;">'
    + `<a href="${simulUrl}" target="_blank" style="display:inline-block;background:#FF8617;color:#ffffff;text-decoration:none;font-weight:bold;padding:12px 22px;border-radius:10px;font-size:14px;">📦 Estimer les frais de réexpédition</a>`
    + '</p>'
    + '<p style="font-size:13px;color:#6b5d52;">Si vous ne souhaitez pas récupérer cet objet, vous pouvez nous en informer par retour de mail — il sera alors remis à une association caritative (<strong>Espoir</strong> ou <strong>Emmaüs</strong> Colmar).</p>'
    + '<p style="margin-top:18px;">Bien cordialement,<br><strong>La Conciergerie</strong><br>'
    + '<span style="color:#6b5d52;font-size:12px;">Service de conciergerie — Colmar, Alsace<br>'
    + 'contact@conciergerie-alsace.com</span></p>'
    + '</div>';

  const r = await fetch(`${SUPA_URL}/functions/v1/send-ot-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${SUPA_TOKEN || SUPA_KEY}`,
      'apikey': SUPA_KEY,
    },
    body: JSON.stringify({
      toEmail,
      toName: toName || '',
      subject: `La Conciergerie — Objet trouvé : ${objet.desc || objet.ref || 'votre objet'}`,
      htmlContent,
      replyTo: 'client@conciergerie-alsace.com',
    }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data?.error || data?.details?.message || `HTTP ${r.status}`);
  return true;
}

// Page publique simulateur — rendue SANS authentification
// Accès : https://app.conciergerie-alsace.com/exp/TOKEN
function SimulateurExpPublic({ token }) {
  const [objets,  setObjets]  = useState(null);
  const [tarifs,  setTarifs]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // Poids / pays / service — poids calculé après résolution de `objet`
  const [pays,   setPays]   = useState('');
  const [transp, setTransp] = useState('colissimo');
  const [zoneManuelle, setZoneManuelle] = useState('');

  useEffect(() => {
    // Lecture via Edge Function get-ot-public (clé service, contourne RLS)
    // Retourne uniquement les données publiques de l'objet identifié par token
    const load = async () => {
      try {
        const r = await fetch(
          `${SUPA_URL}/functions/v1/get-ot-public?token=${encodeURIComponent(token)}`,
          { headers: { 'apikey': SUPA_KEY } }
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!data.found) {
          setObjets([]);
        } else {
          // Reconstituer un tableau d'objets minimal pour la recherche par token
          setObjets([data.objet]);
          if (data.tarifs) setTarifs(data.tarifs);
        }
      } catch(e) {
        setError('Impossible de charger les données. Veuillez réessayer.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  if (loading) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center',
      fontFamily:'system-ui,sans-serif', color:'#2a2520' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ fontSize:32, marginBottom:12 }}>📦</div>
        <div>Chargement…</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center',
      fontFamily:'system-ui,sans-serif', color:'#2a2520', padding:24 }}>
      <div style={{ textAlign:'center', maxWidth:400 }}>
        <div style={{ fontSize:32, marginBottom:12 }}>⚠️</div>
        <div style={{ marginBottom:12 }}>{error}</div>
        <div style={{ fontSize:13, color:'#888' }}>
          Contactez-nous : <a href="mailto:contact@conciergerie-alsace.com">contact@conciergerie-alsace.com</a>
        </div>
      </div>
    </div>
  );

  const objet = (objets || []).find(o => o.expToken === token);
  const poids = objet?.poids ? String(objet.poids) : '';

  if (!objet) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center',
      fontFamily:'system-ui,sans-serif', color:'#2a2520', padding:24 }}>
      <div style={{ textAlign:'center', maxWidth:400 }}>
        <div style={{ fontSize:32, marginBottom:12 }}>🔍</div>
        <p>Lien invalide ou expiré.</p>
        <p style={{ fontSize:13, color:'#888' }}>
          Contactez La Conciergerie :<br/>
          <a href="mailto:contact@conciergerie-alsace.com">contact@conciergerie-alsace.com</a>
        </p>
      </div>
    </div>
  );

  // bienNom déjà résolu côté Edge Function
  const bienNom = objet.bienNom || null;
  const t = tarifs || TARIFS_EXP_DEFAULT;
  const paysDef = PAYS_ZONES.find(p => p.label === pays);
  const isAutre = paysDef?.colissimo === null;
  const zone = isAutre
    ? (zoneManuelle || null)
    : (transp === 'colissimo' ? paysDef?.colissimo : paysDef?.chronopost) || null;
  const devis = (poids && zone) ? calcDevis(Number(poids), zone, transp, t) : null;
  const serviceLabel = transp === 'colissimo'
    ? (COLISSIMO_LABELS[zone] || 'Colissimo International')
    : (CHRONOPOST_LABELS[zone] || 'Chronopost Express International');

  const or = '#FF8617';
  const card = { background:'#fff', borderRadius:14, padding:'16px 20px',
    boxShadow:'0 2px 12px rgba(0,0,0,0.08)', marginBottom:16 };

  return (
    <div style={{ minHeight:'100vh', background:'#faf7f4', fontFamily:'system-ui,sans-serif',
      color:'#2a2520', padding:'24px 16px' }}>
      <div style={{ maxWidth:520, margin:'0 auto' }}>

        {/* Header */}
        <div style={{ textAlign:'center', marginBottom:24 }}>
          <div style={{ fontSize:13, fontWeight:700, letterSpacing:2,
            textTransform:'uppercase', color:or, marginBottom:4 }}>
            La Conciergerie · Colmar
          </div>
          <h1 style={{ fontSize:22, fontWeight:800, margin:'0 0 6px' }}>Réexpédition d'objet trouvé</h1>
          <p style={{ fontSize:13, color:'#888', margin:0 }}>Simulateur de frais — devis indicatif</p>
        </div>

        {/* Fiche objet */}
        <div style={card}>
          <div style={{ fontWeight:700, marginBottom:10, color:or }}>📋 Votre objet</div>
          <div style={{ fontSize:14, lineHeight:1.8 }}>
            <div><strong>Description :</strong> {objet.desc || '—'}</div>
            <div><strong>Référence :</strong> {objet.ref || '—'}</div>
            {bienNom && <div><strong>Logement :</strong> {bienNom}</div>}
            {objet.date && <div><strong>Découvert le :</strong> {objet.date}</div>}
            {objet.poids && <div><strong>Poids estimé :</strong> {objet.poids} g</div>}
          </div>
        </div>

        {/* Simulateur */}
        <div style={card}>
          <div style={{ fontWeight:700, marginBottom:12, color:or }}>💶 Simuler les frais d'expédition</div>

          <div style={{ marginBottom:10 }}>
            <label style={{ display:'block', fontSize:12, fontWeight:600, marginBottom:4 }}>
              Poids conditionné (grammes)
            </label>
            <div style={{ padding:'10px 12px', border:'1.5px solid #eee', borderRadius:8,
              fontSize:14, background:'#f5f5f5', color:'#555' }}>
              {objet.poids ? `${objet.poids} g` : '—'}
            </div>
          </div>

          <div style={{ marginBottom:10 }}>
            <label style={{ display:'block', fontSize:12, fontWeight:600, marginBottom:4 }}>
              Service d'expédition
            </label>
            <div style={{ display:'flex', gap:8 }}>
              {[
                { v:'colissimo',   l:'📫 Colissimo',   sub:'Standard · J+3 à J+8' },
                { v:'chronopost',  l:'⚡ Chronopost',  sub:'Express · J+1 à J+5' },
              ].map(s => (
                <div key={s.v} onClick={() => setTransp(s.v)}
                  style={{ flex:1, padding:'10px 12px', borderRadius:10, cursor:'pointer',
                    border: transp === s.v ? `2px solid ${or}` : '2px solid #eee',
                    background: transp === s.v ? '#fff8f2' : '#fff' }}>
                  <div style={{ fontWeight:700, fontSize:13 }}>{s.l}</div>
                  <div style={{ fontSize:11, color:'#888', marginTop:2 }}>{s.sub}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom:10 }}>
            <label style={{ display:'block', fontSize:12, fontWeight:600, marginBottom:4 }}>
              Pays de destination
            </label>
            <select value={pays} onChange={e => { setPays(e.target.value); setZoneManuelle(''); }}
              style={{ width:'100%', padding:'10px 12px', border:'1.5px solid #ddd',
                borderRadius:8, fontSize:14, boxSizing:'border-box' }}>
              <option value="">— Choisir un pays —</option>
              {PAYS_ZONES.map(p => (
                <option key={p.label} value={p.label}>{p.label}</option>
              ))}
            </select>
          </div>

          {isAutre && (
            <div style={{ marginBottom:10 }}>
              <label style={{ display:'block', fontSize:12, fontWeight:600, marginBottom:4 }}>
                Zone {transp === 'colissimo' ? '(france / europe / mondeB / mondeC…)' : '(z1 à z9)'}
              </label>
              <input type="text" value={zoneManuelle}
                onChange={e => setZoneManuelle(e.target.value.toLowerCase())}
                placeholder={transp === 'colissimo' ? 'ex: mondeB' : 'ex: z4'}
                style={{ width:'100%', padding:'10px 12px', border:'1.5px solid #ddd',
                  borderRadius:8, fontSize:14, boxSizing:'border-box' }} />
            </div>
          )}

          {/* Résultat */}
          {devis && !devis.overMax && (
            <div style={{ background:'#fff8f2', border:`1.5px solid ${or}`, borderRadius:10,
              padding:'14px 16px', marginTop:12 }}>
              <div style={{ fontSize:12, color:'#888', marginBottom:4 }}>{serviceLabel}</div>
              <div style={{ fontSize:12, color:'#aaa', marginBottom:8 }}>Tranche : {devis.tranche}</div>
              <div style={{ display:'flex', alignItems:'baseline', gap:8 }}>
                <span style={{ fontSize:28, fontWeight:800, color:or }}>{devis.prixTTC} €</span>
                <span style={{ fontSize:13, color:'#888' }}>TTC</span>
              </div>
              <div style={{ fontSize:12, color:'#aaa', marginTop:4 }}>
                {devis.prixHT} € HT · TVA 20% incluse · Frais de traitement et d'emballage inclus
              </div>
            </div>
          )}
          {devis?.overMax && (
            <div style={{ background:'#fff3cd', borderRadius:10, padding:'12px 16px',
              marginTop:12, fontSize:13, color:'#856404' }}>
              ⚠️ Poids supérieur à 10 kg — tarif sur devis.<br/>
              Contactez-nous : <a href="mailto:contact@conciergerie-alsace.com">contact@conciergerie-alsace.com</a>
            </div>
          )}
          {poids && pays && !zone && !isAutre && (
            <div style={{ fontSize:12, color:'#c0392b', marginTop:8 }}>
              Ce pays n'est pas desservi par {transp === 'colissimo' ? 'Colissimo' : 'Chronopost'}. Essayez l'autre service.
            </div>
          )}
        </div>

        {/* Option don */}
        <div style={{ ...card, background:'#f0f7ee', border:'1.5px solid #86BD92' }}>
          <div style={{ fontWeight:700, marginBottom:6 }}>🤝 Je renonce à récupérer mon objet</div>
          <p style={{ fontSize:13, color:'#4a6644', margin:'0 0 8px' }}>
            Si vous ne souhaitez pas récupérer cet objet, il sera remis à une association caritative
            (<strong>Espoir</strong> ou <strong>Emmaüs</strong> Colmar). Informez-nous simplement par email.
          </p>
          <a href={`mailto:contact@conciergerie-alsace.com?subject=${encodeURIComponent(`Renonciation — Objet trouvé ${objet.ref ? `réf. ${objet.ref}` : ''}`)}&body=${encodeURIComponent(`Bonjour,

Je soussigné(e) renonce à récupérer l'objet trouvé suivant :

- Description : ${objet.desc || '—'}
- Référence : ${objet.ref || '—'}${bienNom ? `
- Logement : ${bienNom}` : ''}${objet.date ? `
- Découvert le : ${objet.date}` : ''}

Je vous autorise à remettre cet objet à une association caritative (Espoir ou Emmaüs Colmar) ou à en disposer selon votre procédure habituelle.

Cordialement,`)}`}
            style={{ display:'inline-block', background:'#86BD92', color:'white',
              textDecoration:'none', fontWeight:700, padding:'10px 18px',
              borderRadius:8, fontSize:13 }}>
            ✉️ M'en dessaisir par email
          </a>
        </div>

        {/* Contact */}
        <div style={{ textAlign:'center', fontSize:12, color:'#aaa', marginTop:16 }}>
          <strong>La Conciergerie</strong> · 10 rue de la porte neuve, 68000 Colmar<br/>
          <a href="mailto:contact@conciergerie-alsace.com" style={{ color:or }}>contact@conciergerie-alsace.com</a>
          <br/><br/>
          <em>Devis indicatif, valable 30 jours. Tarifs La Poste 2026.</em>
        </div>

      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════

export { SecObjets, AdminGrilleTarifs, SimulateurExpPublic, RecoveryScreen, LoginScreen, getFrenchVoice, cleanForSpeech, speak, getReadableText, useVoiceMode, VoiceListener, VoiceToggleButton, PendingReportBanner, AssistanceBanner, DureeAnomBanner, SyncErrorBanner, ChangePasswordModal, NotificationButton, NotifRoleChip, NotifChannelChip, NotifEventRow, SecCalendrier, SecNotifs, useNotifToasts, NotifToastStack, NotifToastItem };
